import {
    CountdownData,
    MenuCategoryData,
    MenuCompositeData,
    MenuData,
    MenuDietaryIndicatorsData,
    MenuEntityModel,
    MenuExtraGroupData,
    MenuExtraItemsData,
    MenuExtraItemsMenuItemData,
    MenuExtraProductData,
    MenuItem,
    MenuItemData,
    MenuItemSellableData,
    MenuItemState,
    MenuPlgData,
    MenuPlgItemsData,
    MenuPlgItemsMenuItemData,
    MenuPlgSellableData,
    MenuProductExtraUsage,
    MenuProductLinePlgQtyData,
    MenuProductLineQtyData,
    MenuQtyData,
    MenuSellableCategoryLookupData,
    MenuSellableCustomObjectData,
    MenuSellableData,
    MenuSellableMetaTagDefinitionData,
    MergedScheduleData,
    OrderTypeInfoData,
    PairedItemData,
    PairingData,
    PairingSetData,
    ScheduleData,
} from "./menu-entity.model";
import {
    computed,
    DestroyRef,
    effect,
    inject,
    Injectable,
    linkedSignal,
    signal,
    Signal,
    WritableSignal
} from "@angular/core";
import {BehaviorSubject, concatMap, debounceTime, Observable, Subject, Subscriber} from "rxjs";
import {ResponsiveService} from "../responsive/responsive.service";
import {AnalyticsService} from '../logging/analytics.service';
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {OrderType} from '../order/order';
import {OrderModel} from '../order/order.model';
import {EntityData, FedeltaImageData} from "@fedelta-pos/data-client";
import {Color} from "../ui-elements/color";
import {CssVarHelper} from '../utils/css-var.helper';
import {PromoModel} from '../promo/promo.model';
import {IdentityModel} from '../identity/identity.model';
import {AppPromoData, AppPromoPromotedSellableData} from '../identity/member-summary-entity.model';
import {SellableSizesData, SellableSizesSearchModel} from "./sellable-sizes-search.model";
import {ActiveSchedulesSearchModel} from '../schedule/active-schedules-search.model';
import {StoreData} from '../store/store-search.model';
import {TranslateService} from "@ngx-translate/core";
import {Levenshtein} from '../utils/levenshtein';
import {onEach} from '../utils/observable-utils';
import {ConfigurationModel} from '../configuration/configuration.model';
import {effectOnSignalSet} from '../utils/signal-utils';
import {DeferredInjectable} from '../deferred-injection.service';
import {SessionModel} from '../session/session.model';
import {NavigationService} from '../navigation/navigation.service';
import {LoadingService} from '../ui-elements/loading/loading.service';
import {TableStatusManageRsaService} from './table-status-manage-rsa.service';

class BuildRequest {
    public requestCount: number = 1;

    constructor(public activatedPromoChanged: boolean = false) {
    }

    update(activatedPromoChanged: boolean): BuildRequest {
        this.activatedPromoChanged = this.activatedPromoChanged || activatedPromoChanged;
        this.requestCount++;
        return this;
    }
}

export class MenuItemInCategory {
    constructor(public menuItem: MenuItemData,
                public category: MenuCategoryData) {
    }
}

export class IndexedMenuItem {
    constructor(public itemInCategory: MenuItemInCategory,
                public weighting: number) {
    }
}

export class MenuItemSize {
    sellableSize: SellableSizesData;
    menuItem: MenuItemData;

    constructor(sellableSize: SellableSizesData, menuItem: MenuItemData) {
        this.sellableSize = sellableSize;
        this.menuItem = menuItem;
    }
}

export interface FindMergedItemForSellableParams {
    sellableId: number;
    orderType: OrderType;
    sizeGroup?: number;
    selectedMenuItem?: MenuItemData;
    /** @default false */
    allowOutOfStock?: boolean;
    /** @default false */
    hideWhenOrdered?: boolean;
    /** @default true */
    initialiseExtras?: boolean;
}

export class MenuCategorySelection {
    constructor(public category: MenuCategoryData,
                public reselected: boolean) {
    }
}

export class MenuItemSelection {

    /**
     * @param menuItem
     * @param category
     * @param orderSimpleProductsDirectly Bypass the item ordering page if the product is simple
     * @param ignoreSizes Do not consider other items in the product size group when checking for a simple product
     * @param requiredExtrasOnly Only consider required extras when checking for a simple product
     * @param simpleProductAdded Callback to indicate that a simple product has been directly ordered
     */
    constructor(public menuItem: MenuItemData,
                public category?: MenuCategoryData,
                public orderSimpleProductsDirectly: boolean = false,
                public ignoreSizes: boolean = false,
                public requiredExtrasOnly: boolean = false,
                public simpleProductAdded: () => void = () => {
                }) {
    }
}

@Injectable({providedIn: "root"})
export class MenuModel implements DeferredInjectable {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private sessionModel: SessionModel = inject(SessionModel);
    private responsiveService: ResponsiveService = inject(ResponsiveService);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private activeSchedulesSearchModel: ActiveSchedulesSearchModel = inject(ActiveSchedulesSearchModel);
    private promoModel: PromoModel = inject(PromoModel);
    private identityModel: IdentityModel = inject(IdentityModel);
    private menuEntityModel: MenuEntityModel = inject(MenuEntityModel);
    private sellableSizesSearchModel: SellableSizesSearchModel = inject(SellableSizesSearchModel);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private translate: TranslateService = inject(TranslateService);
    private navigationService: NavigationService = inject(NavigationService);
    private loadingService: LoadingService = inject(LoadingService);
    private tableStatusManageRsaService: TableStatusManageRsaService = inject(TableStatusManageRsaService);
    private orderModel: OrderModel;

    public static instance: MenuModel;

    private readonly _currentMenu: WritableSignal<MenuData> = signal(undefined);
    public readonly currentMenu: Signal<MenuData> = this._currentMenu.asReadonly();
    public readonly _itemOrderPending: WritableSignal<boolean> = signal(false);
    public readonly itemOrderPending: Signal<boolean> = this._itemOrderPending.asReadonly();

    constructor() {
        console.log("--- MenuModel");

        MenuModel.instance = this;

        let isConstructing: boolean = true;

        effect(() => {
            if (this.responsiveService.desktop()) {
                this.selectDefaultCategory();
            }
        });

        effect(() => this.identifyOrderedPairedItems());

        effect(() => {
            this.pairingSetsPerSellableId();
            if (!isConstructing) {
                this.requestMenuBuild("available pairing sets changed");
            }
        });

        effect(() => {
            this.pairedSellableIdsOrdered();
            this.activePairingSetIds();
            if (!isConstructing) {
                this.requestMenuBuild("ordered pairing items changed");
            }
        });

        onEach(this.sessionModel.sessionReset$, () => {
            this._menuLoaded.set(false);
            this.selectCategory(undefined);
            this._currentMenu.set(undefined);
        });

        /**
         * Enforce sequential lazy initialisation of items
         */
        onEach(this.initialiseMenuItem$.pipe(
            concatMap((menuItem: MenuItemData) => new Observable<MenuItemData>((sub: Subscriber<MenuItemData>) => {
                sub.next(menuItem);
                setTimeout(() => sub.complete());
            }))
        ), async (menuItem: MenuItemData) => {
            this.initialiseItem(menuItem);
            this.menuItemInitialised$.next(menuItem);
        });

        // If the store changes, we rely upon the ordering page to initiate the menu reload,
        // as it coordinates this with the load of the app and stores.
        onEach(this.orderingContextModel.storeDetailsChanged$, () => this.menuLoadPending = true);

        effect(() => {
            if (this.buildPerformedForMemberId && this.buildPerformedForMemberId !== (this.identityModel.currentMember()?.memberId ?? "-1")) {
                this.requestMenuBuild("member id changed");
            }
        });

        effectOnSignalSet(this.promoModel.activatedPromo, () => this.requestMenuBuild("promo activated", true));

        isConstructing = false;
    }

    public inject(): void {
        this.orderModel = inject(OrderModel);

        effect(() => {
            this.promoModel.triggeredSellablesOrdered();
            this.requestMenuBuild("ordered triggered sellables changed");
        });
    }

    /* ==================================================================
        MENU ENTITY MANAGEMENT
        The menu entity is the primary definition of the menu
       ================================================================== */

    private readonly _menuLoaded: WritableSignal<boolean> = signal<boolean>(false);
    public readonly menuLoaded: Signal<boolean> = this._menuLoaded.asReadonly();
    private readonly _pageImage: WritableSignal<FedeltaImageData> = signal<FedeltaImageData>(new FedeltaImageData());
    public readonly pageImage: Signal<FedeltaImageData> = this._pageImage.asReadonly();
    private readonly _pageImageOpacity: WritableSignal<number> = signal<number>(1);
    public readonly pageImageOpacity: Signal<number> = this._pageImageOpacity.asReadonly();

    public readonly rsaBlocked: WritableSignal<boolean> = linkedSignal<boolean>(() => this._currentMenu()?.rsaBlocked ?? false);

    /**
     * Mutable item state cannot be reactively tracked on the menu entity data classes, because they are typically not instantiated
     * as real classes. Instead, they are deserialized from JSON, and for performance reasons do not use the EntityData framework.
     * This means they cannot contain signals (or any other methods), and must be passed around as parameters to MenuModel methods and
     * computed signal functions.
     * Instead, we maintain state in separate classes (e.g. MenuItemState) that are lazily attached to the data objects.
     * The state objects contain reactive signals, while the data objects remain plain.
     */
    public getMenuItemState(menuItem: MenuItemData): MenuItemState {
        if (menuItem.state === undefined) {
            menuItem.state = null;
            menuItem.state = new MenuItemState(menuItem, this, this.menuEntityModel);
        }
        return menuItem.state;
    }

    private menuUnloaded: boolean = false;
    private menuLoadPending: boolean = false;

    private get menuId(): number {
        return this.orderingContextModel.currentStore()?.menuId ?? this.configurationModel.app()?.defaultMenuId ?? null;
    }

    public async loadMenu(reason: string): Promise<void> {
        if (!this.menuId) {
            this.menuLoadPending = false;
            return;
        }

        console.log(Date.now(), "Loading menu:", reason);

        this._menuLoaded.set(false);
        this.menuLoadPending = false;
        try {
            await this.sellableSizesSearchModel.retrieve();
        } catch (error) {
            console.error(error);
            throw error;
        }
        try {
            await this.menuEntityModel.loadEntity(this.menuId + "," +
                (this.orderingContextModel.expectedCompletion() ? (this.orderingContextModel.expectedCompletionAsString()) : "") + "," +
                (this.orderingContextModel.orderType() ? (OrderType.toString(this.orderingContextModel.orderType())) : "") + "," +
                (this.orderingContextModel.currentStore()?.table.tableLookupCode ?? ""));
        } catch (error) {
            this.menuUnloaded = true;
            console.error(error);
            throw error;
        }
        this._menuLoaded.set(true);
        this.menuUnloaded = false;

        // Link up menu items to references in groups
        this.menuEntityModel.dataSignal()?.categories.forEach((category: MenuCategoryData) => {
            category.menuItems.forEach((menuItem: MenuItemData) => {
                if (this.isCompositeItem(menuItem)) {
                    menuItem.sellable.composite.prodLineQuantities.forEach((prodLineQty: MenuProductLineQtyData) => {
                        prodLineQty.productLine.menuItem = this.findAnyMenuItemForSellable()(prodLineQty.productLine.sellableId);
                    });
                }
            });
        });
        this.menuEntityModel.dataSignal()?.extraGroups.forEach((extraGroup: MenuExtraGroupData) => {
            if (extraGroup.extraProducts) {
                extraGroup.extraProducts.forEach((extraProduct: MenuExtraProductData) => {
                    extraProduct.menuItem = this.findAnyExtraItemsMenuItemForSellable()(extraProduct.productLine.sellableId);
                    if (extraProduct.menuItem) {
                        extraProduct.menuItem.sellable.integratedProductImage = extraProduct.productLine.integratedProductImage;
                    }
                });
            } else {
                extraGroup.extraProducts = [];
            }
        });
        this.menuEntityModel.dataSignal()?.plgs.forEach((plg: MenuPlgData) => {
            plg.sellableLines.forEach((plgSellable: MenuPlgSellableData) => {
                plgSellable.menuItem = this.findAnyPlgItemsMenuItemForSellable()(plgSellable.sellable.sellableId);
                if (plgSellable.menuItem) {
                    plgSellable.menuItem.sellable.integratedProductImage = plgSellable.sellable.integratedProductImage;
                    plgSellable.sellable.menuItem = <MenuItemData>plgSellable.menuItem;
                }
            });
        });

        const newQtyAvailable: { [sellableId: number]: number } = {};
        if (this.menuEntityModel.dataSignal()?.countdowns) {
            this.menuEntityModel.dataSignal()?.countdowns.forEach((countdown: CountdownData) => newQtyAvailable[countdown.sellableId] = countdown.qtyAvailable);
        }
        this._sellableQtyAvailable.set(newQtyAvailable);

        this._showCategoryImages.set((this.responsiveService.mobile() ? this.menuEntityModel.dataSignal()?.showMobileCategoryImages : this.menuEntityModel.dataSignal()?.showDesktopCategoryImages) &&
            (!this.configurationModel.isWaiterActor() || this.menuEntityModel.dataSignal()?.showWaiterCategoryImages));
        this._showSubcategoryImages.set((this.responsiveService.mobile() ? this.menuEntityModel.dataSignal()?.showMobileSubcategoryImages : this.menuEntityModel.dataSignal()?.showDesktopSubcategoryImages) &&
            (!this.configurationModel.isWaiterActor() || this.menuEntityModel.dataSignal()?.showWaiterSubcategoryImages));
        this.defaultItemImage.set(this.menuEntityModel.dataSignal()?.defaultItemImage && this.menuEntityModel.dataSignal()?.defaultItemImage.id ? this.menuEntityModel.dataSignal()?.defaultItemImage : undefined);
        this._defaultCategoryImage.set(this.menuEntityModel.dataSignal()?.defaultCategoryImage && this.menuEntityModel.dataSignal()?.defaultCategoryImage.id ? this.menuEntityModel.dataSignal()?.defaultCategoryImage : undefined);
        this._pageImage.set(this.menuEntityModel.dataSignal()?.pageImage && this.menuEntityModel.dataSignal()?.pageImage.id ? this.menuEntityModel.dataSignal()?.pageImage : new FedeltaImageData());
        this._pageImageOpacity.set(this.menuEntityModel.dataSignal()?.pageImageOpacity);

        this.requestMenuBuild("menu loaded");
        this._currentMenu.set(this.menuEntityModel.dataSignal());

        // Update "theme" CSS vars for menu page background image
        const cssVars: Record<string, string | number> = {};
        if (this.menuEntityModel.dataSignal()?.pageImageBackgroundColor) {
            cssVars["--woa-theme-color-menu-page-image-pair-background"] = Color.colorStringToRGB(this.menuEntityModel.dataSignal()?.pageImageBackgroundColor);
            cssVars["--woa-theme-color-menu-page-image-pair-background-shader"] = Color.colorStringToShaderRGB(this.menuEntityModel.dataSignal()?.pageImageBackgroundColor, true);
        }
        if (this.menuEntityModel.dataSignal()?.pageImageTextColor) {
            cssVars["--woa-theme-color-menu-page-image-pair-foreground"] = Color.colorStringToRGB(this.menuEntityModel.dataSignal()?.pageImageTextColor);
            cssVars["--woa-theme-color-menu-page-image-pair-foreground-shader"] = Color.colorStringToShaderRGB(this.menuEntityModel.dataSignal()?.pageImageTextColor);
        }
        CssVarHelper.applyVars(document.documentElement, cssVars);
    }

    public unloadMenu(): void {
        this._menuLoaded.set(false);
        this.menuUnloaded = true;
        this.requestMenuBuild("menu unloaded");
        this._currentMenu.set(undefined);
    }

    public async orderItemWhenMenuLoaded(menuItemSelection: MenuItemSelection): Promise<void> {
        this._itemOrderPending.set(true);
        setTimeout(() => {
            if (this._menuLoaded() && !this.menuLoadPending) {
                this._itemOrderPending.set(false);
                this.orderModel.requestOrderItem$.next(menuItemSelection);
            } else if (!this.menuUnloaded) {

                // Try again in half a second
                this.orderItemWhenMenuLoaded(menuItemSelection);
            }
        }, 500);
    }

    public async setRsaBlock(): Promise<void> {
        if (await this.navigationService.createPage("woa-rsa-block-page")) {
            void this.loadingService.performWithSpinner(async () => {
                try {
                    await this.tableStatusManageRsaService.performAction({
                        waiterName: this.identityModel.waiterName(),
                        storeId: this.orderingContextModel.currentStore().storeId,
                        tableLookupCode: this.orderingContextModel.currentStore().table.tableLookupCode,
                        rsaBlocked: !this.rsaBlocked()
                    });
                    await this.loadMenu("rsa block changed");
                } catch (error) {
                    console.error(error);
                }
            });
        }
    }

    /* ============================================================================
        MENU BUILD
        Construction of the menu presentation model from the menu definition
       ============================================================================ */

    /**
     * Notifies that an activated promoted category has been added to the menu.
     */
    public readonly activatedPromotedCategoryAdded$: Subject<MenuCategoryData> = new Subject();

    private buildRequested$: Subject<void> = new Subject<void>();

    private buildSubscriptionInitialized: boolean = false;
    private latestBuildRequest: BuildRequest;
    private building: boolean = false;
    private buildPerformedForMemberId: string;
    private activatedPromo: AppPromoData;

    private requestMenuBuild(reason: string, activatedPromoChanged: boolean = false): void {
        console.log(Date.now(), "Menu rebuild requested:", activatedPromoChanged, reason);

        // Record the build request. Subsequent requests during a debounce-period are merged.
        this.latestBuildRequest = this.latestBuildRequest ? this.latestBuildRequest.update(activatedPromoChanged) : new BuildRequest(activatedPromoChanged);

        // Subscribe to build requests on the first request
        if (!this.buildSubscriptionInitialized) {
            this.buildSubscriptionInitialized = true;
            onEach(this.buildRequested$.pipe(
                debounceTime(500)
            ), async (): Promise<void> => {

                // Defer concurrent build attempts by re-requesting.
                // As requests are debounced, any further builds triggered by actions DURING build will effectively be batched.
                if (this.building) {
                    console.log(Date.now(), "Deferring concurrent menu build");
                    setTimeout(() => this.buildRequested$.next(), 500);
                    return;
                }

                // Perform the merged build request
                const request: BuildRequest = this.latestBuildRequest;
                this.latestBuildRequest = undefined;
                return this.buildMenu(request);
            }, this.destroyRef);
        }

        // Queue the request to the debouncer
        this.buildRequested$.next();
    }

    private buildMenu(request: BuildRequest): void {
        this.building = true;
        try {
            this._categories.set([]);
            if (!this._menuLoaded()) {
                console.log(Date.now(), "Not performing menu build for " + request.requestCount + " merged request(s) - menu is not yet loaded");
                return;
            }

            console.log(Date.now(), "Performing menu build for " + request.requestCount + " merged request(s)");
            this.buildPerformedForMemberId = this.identityModel.currentMember() ? this.identityModel.currentMember().memberId : "-1";

            this._categories.set([...this.menuEntityModel.dataSignal()?.categories]);

            // For the active pairing sets, build a list of unique active parings for each menu category
            const activePairingsPerCategory: Map<MenuCategoryData, PairingData[]> = new Map<MenuCategoryData, PairingData[]>();
            this.menuEntityModel.dataSignal()?.pairingSets
                .filter((pairingSet: PairingSetData) => this.activePairingSetIds().has(pairingSet.id))
                .forEach((pairingSet: PairingSetData) => {

                    // Find a matching category
                    let targetCategory: MenuCategoryData = this._categories().find((category: MenuCategoryData) => category.name.toUpperCase() === pairingSet.targetCategory.toUpperCase()) ||
                        this._categories().find((category: MenuCategoryData) => category.name.toUpperCase().endsWith("\\" + pairingSet.targetCategory.toUpperCase()));
                    if (!targetCategory) {
                        return;
                    }

                    pairingSet.pairedItems
                        .filter((pairedItem: PairedItemData) => this.pairedSellableIdsOrdered().has(pairedItem.product.sellableId))
                        .forEach((pairedItem: PairedItemData) => pairedItem.pairings.forEach((pairing: PairingData) => {
                            let pairings: PairingData[] = activePairingsPerCategory.get(targetCategory);
                            if (!pairings) {
                                pairings = [pairing];
                                activePairingsPerCategory.set(targetCategory, pairings);
                            } else {
                                const existing: number = pairings.findIndex((p: PairingData) => p.product.sellableId === pairing.product.sellableId);
                                if (existing < 0) {
                                    pairings.push(pairing);
                                } else if (pairings[existing].index > pairing.index) {
                                    pairings.splice(existing, 1, pairing);
                                }
                                pairings.sort((a, b) => a.index - b.index);
                            }
                        }));
                });

            // Add pairing categories
            activePairingsPerCategory.forEach((pairings: PairingData[], category: MenuCategoryData) => this.addPairingCategory(category, this.buildPairingCategory(category, pairings)));

            // Add promoted menu categories
            if (this.identityModel.currentMember()) {
                this.identityModel.currentMember().promos
                    .filter((promo: AppPromoData) =>
                        promo.isAvailableForOrderType(this.orderingContextModel.orderType()) &&
                        (!promo.triggered || this.promoModel.hasOrderedTriggerSellable()(promo)) &&
                        (!this.promoModel.activatedPromo() || promo.id !== this.promoModel.activatedPromo().id) &&
                        promo.displayPromotedMenuCategory &&
                        this.promoModel.canPromoteSellablesAsCategory()(promo))
                    .forEach((promo: AppPromoData) => this.addPromotedCategory(this.buildPromotedCategory(promo),
                        this.responsiveService.desktop() && !this.promoModel.activatedPromo() && !this._selectedCategory()));
            }

            // Add/remove a category for the currently activated promo
            if (this.activatedPromo !== this.promoModel.activatedPromo()) {
                this.activatedPromo = this.promoModel.activatedPromo();
                if (this.activatedPromo) {
                    this.activatedPromotedCategory = this.buildPromotedCategory(this.activatedPromo);
                } else {
                    this.activatedPromotedCategory = undefined;
                }
            }
            if (this.activatedPromotedCategory) {
                this.addPromotedCategory(this.activatedPromotedCategory, request.activatedPromoChanged);
                if (request.activatedPromoChanged) {
                    setTimeout(() => this.activatedPromotedCategoryAdded$.next(this.activatedPromotedCategory));
                }
            }

            this.superCategoryItemCounts().clear();
            this.subToSuperCategories().clear();
            this.categoryDisplayNames().clear();
            this.categoryItems().clear();
            this.categoryImages().clear();
            this.menuItemKeywordIndex.clear();

            this._categories()
                .filter((category: MenuCategoryData) => this.categoryIsAvailable()(category))
                .forEach((category: MenuCategoryData) => {

                    // Standardise category name case, and adjust subcategory names to match their super-category.
                    this.categoryDisplayNames().set(category, this.categoryTitleCase(category.name));

                    // Get the filtered list of items for each category.
                    const menuItems: MenuItemData[] = category.menuItems.filter((menuItem: MenuItemData) =>
                        !menuItem.hidden &&
                        !this.isSuppressedItem(menuItem) &&
                        this.itemIsAvailable()(menuItem) &&
                        !(this.menuEntityModel.dataSignal()?.hideOutOfStock && this.itemOutOfStock()(menuItem)));
                    menuItems.sort((a, b) => Number(this.promoModel.isFeaturedSellable()(b.sellable.sellableId)) - Number(this.promoModel.isFeaturedSellable()(a.sellable.sellableId)));
                    this.categoryItems().set(category, menuItems);

                    // Accumulate the number of visible items in the super categories.
                    // Search for subcategories and build a map to relate them to their super categories.
                    const itemCount: number = menuItems.length;
                    const i: number = category.name.indexOf("\\");
                    if (i > 0) {
                        const superCategoryName: string = category.name.substring(0, i).toLowerCase();
                        this._categories().forEach((superCategory) => {
                            if (superCategory.name.toLowerCase() === superCategoryName) {
                                this.subToSuperCategories().set(category, superCategory);
                                this.categoryDisplayNames().set(category, this.categoryTitleCase(category.name.substring(i + 1)));
                                if (itemCount > 0) {
                                    if (this.superCategoryItemCounts().has(superCategory)) {
                                        this.superCategoryItemCounts().set(superCategory, this.superCategoryItemCounts().get(superCategory) + itemCount);
                                    } else {
                                        this.superCategoryItemCounts().set(superCategory, itemCount);
                                    }
                                }
                            }
                        });
                    }
                });

            // Selectable categories are those without a super category and which have visible items.
            this._selectableCategories.set(this._categories().filter((category: MenuCategoryData) => !this.isSubcategory()(category) && this.categoryHasItems()(category)));

            // Resolve the currently selected category after a reload of the menu
            if (this.responsiveService.desktop()) {
                this.selectDefaultCategory();
            } else if (!this._categories().some((category: MenuCategoryData) => {
                if (category === this._selectedCategory() || category.name === this.selectedCategoryName()) {
                    if (category.id) {
                        this.selectCategory(this.categoryHasItems()(category) ? category : undefined, true);
                    }
                    return true;
                }
                return false;
            })) {
                this.selectCategory(undefined, true);
            }

            // Determine the image (if required) for each category:
            // Use any directly assigned images.
            // Failing that, use the default image if specified.
            // Failing that, use the image of the first item in the category with an image.
            // Failing that, use the image of the first subcategory in the category with an image.
            if (this.menuEntityModel.dataSignal()?.showDesktopCategoryImages || this.menuEntityModel.dataSignal()?.showDesktopSubcategoryImages || this.menuEntityModel.dataSignal()?.showMobileCategoryImages || this.menuEntityModel.dataSignal()?.showMobileSubcategoryImages) {
                this._categories().forEach((category: MenuCategoryData) => {
                    if (this.getMenuCategoryImage(category)) {
                        this.categoryImages().set(category, this.getMenuCategoryImage(category));
                    } else if (this._defaultCategoryImage()) {
                        this.categoryImages().set(category, this._defaultCategoryImage());
                    } else if (this.categoryHasItems()(category, false)) {
                        this.categoryItems().get(category).some((menuItem: MenuItemData) => {
                            const itemImage: FedeltaImageData = this.itemImage()(category, menuItem, !category.id);
                            if (itemImage) {
                                this.categoryImages().set(category, itemImage);
                            }
                            return !!itemImage;
                        });
                    }
                });
                this._categories().forEach((category: MenuCategoryData) => {
                    if (!this.categoryImages().has(category) && this.isSuperCategory()(category)) {
                        Array.from(this.subToSuperCategories().keys()).some((subcategory: MenuCategoryData) => {
                            if (this.subToSuperCategories().get(subcategory) === category) {
                                const image: FedeltaImageData = this.categoryImages().get(subcategory);
                                if (image) {
                                    this.categoryImages().set(category, image);
                                    return true;
                                }
                            }
                            return false;
                        });
                    }
                });
            }

            // Trigger signal updates by assigning copies
            this.superCategoryItemCounts.set(new Map(this.superCategoryItemCounts()));
            this.subToSuperCategories.set(new Map(this.subToSuperCategories()));
            this.categoryDisplayNames.set(new Map(this.categoryDisplayNames()));
            this.categoryItems.set(new Map(this.categoryItems()));
            this.categoryImages.set(new Map(this.categoryImages()));

            this.buildSelectedCategory();
        } finally {
            this.building = false;
        }
    }

    /* ==================================================================
        MENU CATEGORY PRESENTATION
       ================================================================== */

    private readonly _selectableCategories: WritableSignal<MenuCategoryData[]> = signal<MenuCategoryData[]>(undefined);
    public readonly selectableCategories: Signal<MenuCategoryData[]> = this._selectableCategories.asReadonly();
    private readonly _defaultCategoryImage: WritableSignal<FedeltaImageData> = signal<FedeltaImageData>(undefined);
    // noinspection JSUnusedGlobalSymbols
    public readonly defaultCategoryImage: Signal<FedeltaImageData> = this._defaultCategoryImage.asReadonly();
    private readonly _showCategoryImages: WritableSignal<boolean> = signal<boolean>(false);
    public readonly showCategoryImages: Signal<boolean> = this._showCategoryImages.asReadonly();
    private readonly _showSubcategoryImages: WritableSignal<boolean> = signal<boolean>(false);
    public readonly showSubcategoryImages: Signal<boolean> = this._showSubcategoryImages.asReadonly();
    private readonly _categories: WritableSignal<MenuCategoryData[]> = signal<MenuCategoryData[]>([]);
    public readonly categories: Signal<MenuCategoryData[]> = this._categories.asReadonly();

    private readonly subToSuperCategories: WritableSignal<Map<MenuCategoryData, MenuCategoryData>> = signal<Map<MenuCategoryData, MenuCategoryData>>(new Map<MenuCategoryData, MenuCategoryData>());
    private readonly superCategoryItemCounts: WritableSignal<Map<MenuCategoryData, number>> = signal<Map<MenuCategoryData, number>>(new Map());
    private readonly categoryDisplayNames: WritableSignal<Map<MenuCategoryData, string>> = signal<Map<MenuCategoryData, string>>(new Map());
    private readonly categoryImages: WritableSignal<Map<MenuCategoryData, FedeltaImageData>> = signal<Map<MenuCategoryData, FedeltaImageData>>(new Map());
    private readonly categoryItems: WritableSignal<Map<MenuCategoryData, MenuItemData[]>> = signal<Map<MenuCategoryData, MenuItemData[]>>(new Map());

    private readonly isSuperCategory: Signal<(category: MenuCategoryData) => boolean> = computed<(category: MenuCategoryData) => boolean>(() => {
        const superCategoryItemCounts: Map<MenuCategoryData, number> = this.superCategoryItemCounts();
        return (category: MenuCategoryData): boolean => superCategoryItemCounts.has(category);
    });

    private readonly isSubcategory: Signal<(category: MenuCategoryData, superCategory?: MenuCategoryData) => boolean> = computed<(category: MenuCategoryData, superCategory?: MenuCategoryData) => boolean>(() => {
        const subToSuperCategories: Map<MenuCategoryData, MenuCategoryData> = this.subToSuperCategories();
        return (category: MenuCategoryData, superCategory?: MenuCategoryData): boolean =>
            superCategory ? subToSuperCategories.get(category) === superCategory : subToSuperCategories.has(category);
    });

    public readonly getCategoryIconText: Signal<(category: MenuCategoryData) => string> = computed<(category: MenuCategoryData) => string>(() => {
        const categoryDisplayNames: Map<MenuCategoryData, string> = this.categoryDisplayNames();
        return (category: MenuCategoryData): string => {
            const displayName: string = categoryDisplayNames.get(category) || (category ? this.categoryTitleCase(category.name) : undefined);
            return displayName ? displayName.substring(0, 2) : undefined;
        };
    });

    public readonly getCategoryDisplayName: Signal<(category: MenuCategoryData) => string> = computed<(category: MenuCategoryData) => string>(() => {
        const categoryDisplayNames: Map<MenuCategoryData, string> = this.categoryDisplayNames();
        return (category: MenuCategoryData): string =>
            categoryDisplayNames.get(category) || (category ? this.categoryTitleCase(category.name) : undefined);
    });

    public readonly getCategoryPath: Signal<(category: MenuCategoryData) => string> = computed<(category: MenuCategoryData) => string>(() => {
        const isSubcategory: (category: MenuCategoryData, superCategory?: MenuCategoryData) => boolean = this.isSubcategory();
        const getCategoryDisplayName: (category: MenuCategoryData) => string = this.getCategoryDisplayName();
        const subToSuperCategories: Map<MenuCategoryData, MenuCategoryData> = this.subToSuperCategories();
        return (category: MenuCategoryData): string =>
            (isSubcategory(category) ? (getCategoryDisplayName(subToSuperCategories.get(category)) + " ► ") : "") + getCategoryDisplayName(category)
    });

    public readonly getCategoryImage: Signal<(category: MenuCategoryData) => FedeltaImageData> = computed<(category: MenuCategoryData) => FedeltaImageData>(() => {
        const categoryImages: Map<MenuCategoryData, FedeltaImageData> = this.categoryImages();
        return (category: MenuCategoryData): FedeltaImageData => categoryImages.get(category);
    });

    public readonly selectedCategoryWaitTime: Signal<number> = computed<number>(() => this._selectedCategory()?.currentWaitTimeMinutes ?? 0);

    public readonly getCategoryItems: Signal<(category: MenuCategoryData) => MenuItemData[]> = computed<(category: MenuCategoryData) => MenuItemData[]>(() => {
        const categoryItems: Map<MenuCategoryData, MenuItemData[]> = this.categoryItems();
        return (category: MenuCategoryData): MenuItemData[] => categoryItems.get(category);
    });

    private readonly categoryIsAvailableForOrderType: Signal<(category: MenuCategoryData, orderType?: OrderType) => boolean> = computed<(category: MenuCategoryData, orderType?: OrderType) => boolean>(() => {
        const _orderType: OrderType = this.orderingContextModel.orderType();
        return (category: MenuCategoryData, orderType: OrderType = _orderType): boolean =>
            category && (!orderType ||
                ((!category.orderTypes || !category.orderTypes.length || category.orderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))) &&
                    (!category.exclOrderTypes || !category.exclOrderTypes.length || !category.exclOrderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType)))));
    });

    private readonly categoryIsAvailableOnSchedules: Signal<(category: MenuCategoryData) => boolean> = computed<(category: MenuCategoryData) => boolean>(() => {
        const anyMergedScheduleIsActive: (mergedSchedules: MergedScheduleData[]) => boolean = this.anyMergedScheduleIsActive();
        return (category: MenuCategoryData): boolean => category && anyMergedScheduleIsActive(category.mergedSchedules);
    });

    private readonly categoryIsAvailable: Signal<(category: MenuCategoryData) => boolean> = computed<(category: MenuCategoryData) => boolean>(() => {
        const categoryIsAvailableForOrderType: (category: MenuCategoryData, orderType?: OrderType) => boolean = this.categoryIsAvailableForOrderType();
        const categoryIsAvailableOnSchedules: (category: MenuCategoryData) => boolean = this.categoryIsAvailableOnSchedules();
        return (category: MenuCategoryData): boolean =>
            !category.id || (!category.hidden &&
                categoryIsAvailableForOrderType(category) &&
                categoryIsAvailableOnSchedules(category));
    });

    private readonly categoryHasItems: Signal<(category: MenuCategoryData, includeSubCategories?: boolean) => boolean> = computed<(category: MenuCategoryData, includeSubCategories?: boolean) => boolean>(() => {
        const categoryItems: Map<MenuCategoryData, MenuItemData[]> = this.categoryItems();
        const superCategoryItemCounts: Map<MenuCategoryData, number> = this.superCategoryItemCounts();
        const categoryIsAvailable: (category: MenuCategoryData) => boolean = this.categoryIsAvailable();
        const isSuperCategory: (category: MenuCategoryData) => boolean = this.isSuperCategory();
        return (category: MenuCategoryData, includeSubCategories: boolean = true): boolean =>
            !categoryIsAvailable(category) ? false
                : categoryItems.has(category) && categoryItems.get(category).length ? true
                    : includeSubCategories && isSuperCategory(category) ? superCategoryItemCounts.get(category) > 0
                        : false;
    });

    private categoryTitleCase(categoryName: string) {
        categoryName = categoryName.trim();

        // Only convert categories which are not already mixed-case
        return categoryName[0] === "`" ? categoryName.slice(1)
            : categoryName !== categoryName.toUpperCase() && categoryName !== categoryName.toLowerCase() ? categoryName
                : categoryName.split(/\s+/).map((word: string, i: number) =>
                    ["IPA", "XPA", "NEIPA", "RTD"].indexOf(word) >= 0 ? word
                        : i > 0 && ["and", "or", "in", "of", "on"].indexOf(word.toLowerCase()) >= 0 ? word.toLowerCase()
                            : word[0].toUpperCase() + word.slice(1).toLowerCase()).join(" ");
    }

    public getMenuCategoryDescription(category: MenuCategoryData): string {
        return category?.description ?? category?.integratedMenuCategoryDescription;
    }

    public getMenuCategoryImage(category: MenuCategoryData): FedeltaImageData {
        return category?.categoryImage?.id || category?.categoryImage?.image ? category.categoryImage
            : category?.integratedMenuCategoryImage?.id || category?.integratedMenuCategoryImage?.image ? category.integratedMenuCategoryImage
                : undefined;
    }

    /* ==================================================================
        PAIRING AND PROMOTED CATEGORIES
       ================================================================== */

    private readonly pairedSellableIdsOrdered: WritableSignal<Set<number>> = signal(new Set<number>());
    private readonly activePairingSetIds: WritableSignal<Set<number>> = signal(new Set<number>());

    /**
     * Indexes the currently available pairing sets by the sellableIds of paired items so that when the order changes,
     * we can quickly determine when the menu needs to be rebuilt.
     */
    private readonly pairingSetsPerSellableId: Signal<Map<number, Set<PairingSetData>>> = computed(() =>
        this.menuEntityModel.dataSignal()?.pairingSets
            ?.filter((pairingSet: PairingSetData) => this.anyScheduleIsActive()(pairingSet.schedules))
            .flatMap((pairingSet: PairingSetData) => pairingSet.pairedItems.map((pairedItem: PairedItemData) => {
                pairedItem.pairings.forEach((pairing: PairingData, index: number) => {
                    pairing.index = index;
                    pairing.pairingSet = pairingSet;
                });
                return {sellableId: pairedItem.product.sellableId, pairingSet};
            }))
            .reduce((map: Map<number, Set<PairingSetData>>, item: {
                sellableId: number,
                pairingSet: PairingSetData
            }) => {
                (map.get(item.sellableId) ?? map.set(item.sellableId, new Set<PairingSetData>()).get(item.sellableId))
                    .add(item.pairingSet);
                return map;
            }, new Map<number, Set<PairingSetData>>()) ?? new Map<number, Set<PairingSetData>>());

    private readonly defaultPromoImage: Signal<FedeltaImageData> = computed<FedeltaImageData>(() => {
        if (!this.configurationModel.app()?.defaultImage) {
            return undefined;
        }
        const image: FedeltaImageData = EntityData.create(FedeltaImageData);
        image.id = 0;
        image.image = this.configurationModel.app()?.defaultImage;
        return image;
    });

    private activatedPromotedCategory: MenuCategoryData;

    /**
     * Identifies active pairing sets and paired sellable ids in the current order.
     */
    private identifyOrderedPairedItems(): void {
        const pairedSellableIdsOrdered: Set<number> = this.menuEntityModel.dataSignal()?.pairingSets
            ? new Set(this.orderModel.orderedSellableIds().filter((sellableId: number) => this.pairingSetsPerSellableId().has(sellableId)))
            : new Set<number>();
        const activePairingSetIds: Set<number> = new Set([...pairedSellableIdsOrdered]
            .flatMap((sellableId: number) => [...this.pairingSetsPerSellableId().get(sellableId)])
            .map((pairingSet: PairingSetData) => pairingSet.id));

        if (pairedSellableIdsOrdered.size != this.pairedSellableIdsOrdered().size ||
            [...pairedSellableIdsOrdered].some((sellableId: number) => !this.pairedSellableIdsOrdered().has(sellableId))) {
            this.pairedSellableIdsOrdered.set(pairedSellableIdsOrdered);
        }
        if (activePairingSetIds.size != this.activePairingSetIds().size ||
            [...activePairingSetIds].some((pairingSetId: number) => !this.activePairingSetIds().has(pairingSetId))) {
            this.activePairingSetIds.set(activePairingSetIds);
        }
    }

    private buildPairingCategory(targetCategory: MenuCategoryData, pairings: PairingData[]): MenuCategoryData {
        let pairingSet: PairingSetData;
        const category: MenuCategoryData = new MenuCategoryData();
        let pairingCnt: number = 0;
        category.menuItems = pairings
            .map((pairing: PairingData) => {
                if (this.configurationModel.app()?.pairingLimit != undefined && pairingCnt >= this.configurationModel.app()?.pairingLimit) {
                    return undefined;
                }
                const menuItem: MenuItemData = this.findMergedItemForSellable()({
                    sellableId: pairing.product.sellableId,
                    orderType: this.orderingContextModel.orderType()
                });
                if (menuItem) {
                    menuItem.pairingNotes = pairing.notes;
                    if (pairing.pairingSet && !pairingSet) {
                        pairingSet = pairing.pairingSet;
                    }
                    pairingCnt++;
                }
                return menuItem;
            })
            .filter((menuItem: MenuItemData) => menuItem);
        if (!category.menuItems.length) {
            return undefined;
        }

        let targetCategoryName: string = targetCategory.name;
        let superCategoryName: string = targetCategoryName;
        const i: number = targetCategoryName.indexOf("\\");
        if (i > 0) {
            superCategoryName = targetCategoryName.substring(0, i);
            targetCategoryName = targetCategoryName.substring(i + 1);
        }

        category.id = 1000 + targetCategory.id;
        category.name = superCategoryName + "\\" + (pairingSet && pairingSet.categoryTitle ? pairingSet.categoryTitle : this.translate.instant("woa.message.pairings.categoryName", {"category": targetCategoryName})).toLowerCase();
        category.description = pairingSet && pairingSet.categoryDescription ? pairingSet.categoryDescription : this.translate.instant("woa.message.pairings.categoryDescription", {"category": targetCategoryName.toLowerCase()});
        category.mergedSchedules = [];
        category.pairingsCategory = true;

        void this.analyticsService.trackPairingsActivated();

        return category;
    }

    private addPairingCategory(targetCategory: MenuCategoryData, category: MenuCategoryData): void {
        if (category) {
            this._categories.update(cats => {
                const i: number = cats.indexOf(targetCategory);
                cats.splice(i < 0 ? 0 : i, 0, category);
                return cats;
            });
        }
    }

    private buildPromotedCategory(promo: AppPromoData): MenuCategoryData {
        const category: MenuCategoryData = new MenuCategoryData();
        category.informational = promo.promotedCategoryInformational;
        category.menuItems = promo.promotedSellables
            .map((promoted: AppPromoPromotedSellableData) => this.findMergedItemForSellable()({
                sellableId: promoted.sellable.sellableId,
                orderType: this.orderingContextModel.orderType(),
                allowOutOfStock: true,
                hideWhenOrdered: promo.suppressCategoryProducts && !promoted.promoteIfOrdered
            }))
            .filter((menuItem: MenuItemData) => menuItem);
        if (!category.menuItems.length) {
            return undefined;
        }
        category.id = promo === this.promoModel.activatedPromo() ? undefined : -1;
        category.name = promo.categoryTitle || promo.appText1 || promo.name;
        category.description = promo.showDescriptionOnCategory ? (promo.categoryDescription || promo.appText2) : undefined;
        category.categoryImage = !promo.showImageOnCategory ? undefined
            : promo.categoryImage && promo.categoryImage.id ? promo.categoryImage
                : promo.image && promo.image.id ? promo.image
                    : this.defaultPromoImage();
        category.mergedSchedules = [];
        category.hasHideWhenOrderedItems = category.menuItems.some((menuItem: MenuItemData) => menuItem.hideWhenOrdered);
        category.itemFeatureTag = promo.menuItemFeatureTag;
        category.featured = true;
        return category;
    }

    private addPromotedCategory(category: MenuCategoryData, select: boolean): void {
        if (category) {
            this._categories.update(cats => {
                cats.unshift(category);
                return cats;
            });
            if (select) {
                this.selectCategory(category);
            }
        }
    }

    public deactivatePromotedCategory(): void {
        if (this.activatedPromotedCategory) {
            this.promoModel.activatedPromo.set(undefined);
            this.requestMenuBuild("promo deactivated");
        }
    }

    /* ==================================================================
        MENU ITEM PRESENTATION
       ================================================================== */

    public readonly defaultItemImage: WritableSignal<FedeltaImageData> = signal<FedeltaImageData>(undefined);
    private _sellableQtyAvailable: WritableSignal<{ [sellableId: number]: number }> = signal<{
        [sellableId: number]: number
    }>({});

    public readonly itemName: Signal<(menuItem: MenuItemData) => string> = computed<(menuItem: MenuItemData) => string>(() => {
        const useSizeGroupNames: boolean = this.menuEntityModel.dataSignal()?.useSizeGroupNames;
        const getItemSellableSize: (menuItem: MenuItemData) => SellableSizesData = this.itemSellableSize();
        return (menuItem: MenuItemData): string => {
            if (!menuItem) {
                return undefined;
            }
            if (menuItem.sellableSizeGroup && useSizeGroupNames) {
                const size: SellableSizesData = getItemSellableSize(menuItem);
                if (size && size.externalName) {
                    return size.externalName;
                }
            }
            return this.getMenuItemDisplayName(menuItem);
        };
    });

    public readonly itemIconText: Signal<(menuItem: MenuItemData) => string> = computed<(menuItem: MenuItemData) => string>(() => {
        const getItemName: (menuItem: MenuItemData) => string = this.itemName();
        return (menuItem: MenuItemData): string => getItemName(menuItem).substring(0, 2);
    });

    public readonly itemImage: Signal<(category: MenuCategoryData, menuItem: MenuItem, forPromo?: boolean, useDefault?: boolean) => FedeltaImageData> = computed<(category: MenuCategoryData, menuItem: MenuItem, forPromo?: boolean, useDefault?: boolean) => FedeltaImageData>(() => {
        const standardItemImage: (menuItem: MenuItem, useDefault?: boolean) => FedeltaImageData = this.standardItemImage();
        const promotionalImage: (category: MenuCategoryData, sellableId: number, forPromo?: boolean) => FedeltaImageData = this.promoModel.promotionalImage();
        return (category: MenuCategoryData, menuItem: MenuItem, forPromo?: boolean, useDefault: boolean = false): FedeltaImageData =>
            !menuItem ? undefined
                : promotionalImage(category, menuItem.sellable.sellableId, forPromo) || standardItemImage(menuItem, useDefault);
    });

    public readonly standardItemImage: Signal<(menuItem: MenuItem, useDefault?: boolean) => FedeltaImageData> = computed<(menuItem: MenuItem, useDefault?: boolean) => FedeltaImageData>(() => {
        const defaultItemImage: FedeltaImageData = this.defaultItemImage();
        return (menuItem: MenuItem, useDefault: boolean = false): FedeltaImageData => !menuItem ? undefined : (this.getMenuItemProductImage(menuItem) ?? (useDefault ? defaultItemImage : undefined));
    });

    public readonly itemOutOfStock: Signal<(menuItem: MenuItem) => boolean> = computed<(menuItem: MenuItem) => boolean>(() => {
        const sellableOutOfStock: (sellableId: number) => boolean = this.sellableOutOfStock();
        const checkItemOutOfStock = (menuItem: MenuItem): boolean => {
            if (menuItem && this.isNormalMenuItem(menuItem)) {
                this.initialiseItem(<MenuItemData>menuItem);
            }
            return menuItem && menuItem.sellable && (sellableOutOfStock(menuItem.sellable.sellableId) ||
                (this.isNormalMenuItem(menuItem) && (<MenuItemData>menuItem).sellable.composite &&
                    (<MenuItemData>menuItem).sellable.composite.prodLineQuantities.some((prodLineQty: MenuProductLineQtyData) => checkItemOutOfStock(prodLineQty.productLine.menuItem))));
        };
        return checkItemOutOfStock;
    });

    public readonly allSizesOutOfStock: Signal<(menuItem: MenuItemData) => boolean> = computed<(menuItem: MenuItemData) => boolean>(() => {
        const itemOutOfStock: (menuItem: MenuItem) => boolean = this.itemOutOfStock();
        const getItemSizes: (menuItem: MenuItemData) => MenuItemSize[] = this.itemSizes();
        return (menuItem: MenuItemData): boolean => {
            const sizes: MenuItemSize[] = getItemSizes(menuItem);
            return sizes && sizes.length >= 2 ? sizes.every((size: MenuItemSize) => itemOutOfStock(size.menuItem)) : itemOutOfStock(menuItem);
        };
    });

    public readonly sellableIsAvailable: Signal<(sellableId: number) => boolean> = computed<(sellableId: number) => boolean>(() => {
        const findAnyMenuItemForSellable: (sellableId: number) => MenuItemData = this.findAnyMenuItemForSellable();
        return (sellableId: number): boolean => sellableId && !!findAnyMenuItemForSellable(sellableId);
    });

    public readonly itemIsAvailableOnSchedules: Signal<(menuItem: MenuItemData) => boolean> = computed<(menuItem: MenuItemData) => boolean>(() => {
        const anyMergedScheduleIsActive: (mergedSchedules: MergedScheduleData[]) => boolean = this.anyMergedScheduleIsActive();
        return (menuItem: MenuItemData): boolean => menuItem && anyMergedScheduleIsActive(menuItem.mergedSchedules);
    });

    public readonly itemIsAvailableForOrderType: Signal<(menuItem: MenuItemData, orderType?: OrderType) => boolean> = computed<(menuItem: MenuItemData, orderType?: OrderType) => boolean>(() => {
        const _orderType: OrderType = this.orderingContextModel.orderType();
        return (menuItem: MenuItemData, orderType: OrderType = _orderType): boolean =>
            menuItem && (!orderType ||
                ((!menuItem.orderTypes || !menuItem.orderTypes.length || menuItem.orderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))) &&
                    (!menuItem.exclOrderTypes || !menuItem.exclOrderTypes.length || !menuItem.exclOrderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType)))));
    });

    public readonly itemIsAvailable: Signal<(menuItem: MenuItemData) => boolean> = computed<(menuItem: MenuItemData) => boolean>(() => {
        const itemIsAvailableForOrderType: (menuItem: MenuItemData, orderType?: OrderType) => boolean = this.itemIsAvailableForOrderType();
        const itemIsAvailableOnSchedules: (menuItem: MenuItemData) => boolean = this.itemIsAvailableOnSchedules();
        return (menuItem: MenuItemData): boolean =>
            // Note - filtered by store on server
            itemIsAvailableForOrderType(menuItem) &&
            itemIsAvailableOnSchedules(menuItem);
    });

    public readonly sellableQtyAvailable: Signal<(sellableId: number) => number> = computed<(sellableId: number) => number>(() => {
        const qtyAvailable: { [sellableId: number]: number } = this._sellableQtyAvailable();
        const isStoreOpen: boolean = this.orderingContextModel.isStoreOpenAtCompletionTime();
        return (sellableId: number): number => isStoreOpen ? qtyAvailable[sellableId] : undefined;
    });

    public readonly sellableOutOfStock: Signal<(sellableId: number) => boolean> = computed<(sellableId: number) => boolean>(() => {
        const sellableQtyAvailable: (sellableId: number) => number = this.sellableQtyAvailable();
        const findAnyMenuItemForSellable: (sellableId: number) => MenuItemData = this.findAnyMenuItemForSellable();
        return (sellableId: number): boolean => {
            const qtyAvailable: number = sellableQtyAvailable(sellableId);
            const menuItem: MenuItemData = findAnyMenuItemForSellable(sellableId);
            return qtyAvailable != undefined && qtyAvailable < (menuItem ? (menuItem.sellable.minWebOrderQty || 1) : 1);
        };
    });

    public readonly showItemImages: Signal<(category?: MenuCategoryData) => boolean> = computed<(category?: MenuCategoryData) => boolean>(() => {
        const isMobile: boolean = this.responsiveService.mobile();
        const showMobileItemImages: boolean = this.menuEntityModel.dataSignal()?.showMobileItemImages;
        const showDesktopItemImages: boolean = this.menuEntityModel.dataSignal()?.showDesktopItemImages;
        const isWaiterActor: boolean = this.configurationModel.isWaiterActor();
        const showWaiterItemImages: boolean = this.menuEntityModel.dataSignal()?.showWaiterItemImages;
        const subToSuperCategories: Map<MenuCategoryData, MenuCategoryData> = this.subToSuperCategories();
        const selectedCategory: MenuCategoryData = this._selectedCategory();
        return (category: MenuCategoryData = selectedCategory): boolean =>
            (isMobile ? showMobileItemImages : showDesktopItemImages) &&
            (!isWaiterActor || showWaiterItemImages || (category && (subToSuperCategories.get(category) || category).showWaiterItemImages));
    });

    public readonly rsaAppliesToSellable: Signal<(sellableId: number) => boolean> = computed<(sellableId: number) => boolean>(() => {
        const categories: MenuCategoryData[] = this.menuEntityModel.dataSignal()?.categories;
        return (sellableId: number): boolean =>
            categories.some((category: MenuCategoryData) =>
                category.menuItems.some((menuItem: MenuItemData) => menuItem.sellable.sellableId === sellableId && menuItem.sellable.applyRsa));
    });

    public readonly isSimpleProductItem: Signal<(menuItem: MenuItemData, ignoreSizes?: boolean, requiredExtrasOnly?: boolean) => boolean> = computed<(menuItem: MenuItemData, ignoreSizes?: boolean, requiredExtrasOnly?: boolean) => boolean>(() => {
        const findSizesForItem: (menuItem: MenuItemData) => MenuItemSize[] = this.findSizesForItem();
        return (menuItem: MenuItemData, ignoreSizes: boolean = false, requiredExtrasOnly: boolean = false): boolean => {
            this.initialiseItem(menuItem);
            if (menuItem.sellableSizeGroup && !ignoreSizes) {
                const sizes: MenuItemSize[] = findSizesForItem(menuItem);
                if (sizes && sizes.length) {
                    return !sizes.some((size) => this.isCompositeItem(size.menuItem) || (requiredExtrasOnly ? this.itemHasRequiredExtras(menuItem) : this.itemHasVisibleExtras(size.menuItem)));
                }
            }
            return !this.isCompositeItem(menuItem) && !(requiredExtrasOnly ? this.itemHasRequiredExtras(menuItem) : this.itemHasVisibleExtras(menuItem));
        };
    });

    public readonly itemSummaryContainsDetails: Signal<(menuItem: MenuItemData, ignoreSizes?: boolean) => boolean> = computed<(menuItem: MenuItemData, ignoreSizes?: boolean) => boolean>(() => {
        const isSimpleProductItem: (menuItem: MenuItemData, ignoreSizes?: boolean, requiredExtrasOnly?: boolean) => boolean = this.isSimpleProductItem();
        const dietaryStatement: string = this.configurationModel.app()?.dietaryStatement;
        return (menuItem: MenuItemData, ignoreSizes: boolean = false): boolean =>
            (!!menuItem.sellableSizeGroup && isSimpleProductItem(menuItem, ignoreSizes)) ||
            !!this.getMenuItemDescription(menuItem) ||
            menuItem.sellable.dietaryIndicators.length > 0 ||
            !!dietaryStatement;
    });

    public readonly menuContainsItem: Signal<(menuItem: MenuItemData) => boolean> = computed<(menuItem: MenuItemData) => boolean>(() => {
        const categories: MenuCategoryData[] = this.menuEntityModel.dataSignal()?.categories;
        return (menuItem: MenuItemData): boolean =>
            categories.some((category: MenuCategoryData) =>
                category.menuItems.some((existingMenuItem: MenuItemData) => existingMenuItem.id === menuItem.id));
    });

    public readonly findAnyMenuItemForSellable: Signal<(sellableId: number) => MenuItemData> = computed<(sellableId: number) => MenuItemData>(() => {
        const categories: MenuCategoryData[] = this.menuEntityModel.dataSignal()?.categories;
        return (sellableId: number): MenuItemData => {
            let menuItem: MenuItemData = null;
            categories.some((category: MenuCategoryData) => {
                menuItem = category.menuItems.find((item: MenuItemData) => item.sellable.sellableId === sellableId);
                return menuItem != undefined;
            });
            return menuItem;
        };
    });

    public readonly findMergedItemForSellable: Signal<(params: FindMergedItemForSellableParams) => MenuItemData> = computed(() => {
        const categories: MenuCategoryData[] = this.menuEntityModel.dataSignal()?.categories;
        const sellableOutOfStock: (sellableId: number) => boolean = this.sellableOutOfStock();
        const itemIsAvailableForOrderType: (menuItem: MenuItemData, orderType?: OrderType) => boolean = this.itemIsAvailableForOrderType();
        return ({
                    sellableId,
                    orderType,
                    sizeGroup,
                    selectedMenuItem,
                    allowOutOfStock = false,
                    hideWhenOrdered = false,
                    initialiseExtras = true
                }: FindMergedItemForSellableParams): MenuItemData => {
            let menuItems: MenuItemData[] = selectedMenuItem ? [selectedMenuItem] : [];
            categories.forEach((category: MenuCategoryData) =>
                menuItems = menuItems.concat(category.menuItems.filter((menuItem: MenuItemData) =>
                    (selectedMenuItem === undefined || menuItem.id !== selectedMenuItem.id) &&
                    menuItem.sellable.sellableId === sellableId &&
                    (!sellableOutOfStock(sellableId) || allowOutOfStock) &&
                    itemIsAvailableForOrderType(menuItem, orderType))));
            if (sizeGroup != undefined && !menuItems.some((menuItem: MenuItemData) => menuItem.sellableSizeGroup === sizeGroup)) {
                return undefined;
            }
            if (menuItems.length == 0) {
                return undefined;
            }
            this.initialiseItem(menuItems[0], initialiseExtras);
            if (menuItems[0].state.mergedItemForSellable) {
                return menuItems[0].state.mergedItemForSellable;
            }

            const merged: MenuItemData = new MenuItemData();
            merged.id = menuItems[0].id;
            merged.hidden = false;
            merged.hideWhenOrdered = hideWhenOrdered;
            merged.orderTypes = [];
            merged.exclOrderTypes = [];
            const combineSchedules: boolean = menuItems.length > 1 && menuItems.some((menuItem: MenuItemData) => this.itemIsScheduled(menuItem));
            if (combineSchedules) {
                merged.mergedSchedules = [];
            } else {
                merged.mergedSchedules = menuItems[0].mergedSchedules;
            }
            merged.sellable = menuItems[0].sellable;
            merged.stores = [...menuItems[0].stores];
            merged.exclStores = [...menuItems[0].exclStores];
            merged.membershipTypeIds = [...menuItems[0].membershipTypeIds];
            merged.includeGuests = menuItems[0].includeGuests;
            merged.exclMembershipTypeIds = [...menuItems[0].exclMembershipTypeIds];
            merged.excludeGuests = menuItems[0].excludeGuests;
            merged.promotedFeatureTag = menuItems[0].promotedFeatureTag;
            merged.includeOptionOnUntaggedItems = menuItems[0].includeOptionOnUntaggedItems;
            merged.tagItemOnOptionSelection = menuItems[0].tagItemOnOptionSelection;
            merged.informational = menuItems[0].informational;

            menuItems.forEach((menuItem: MenuItemData) => {
                merged.name = merged.name ?? menuItem.name;
                merged.orderTypes = [...new Set([...merged.orderTypes, ...menuItem.orderTypes])];
                merged.exclOrderTypes = [...new Set([...merged.exclOrderTypes, ...menuItem.exclOrderTypes])];
                merged.filterExtras = merged.filterExtras || menuItem.filterExtras;
                merged.filterPlgItems = merged.filterPlgItems || menuItem.filterPlgItems;
                merged.filterTags = merged.filterTags ?? menuItem.filterTags;
                merged.sellableSizeGroup = merged.sellableSizeGroup ?? menuItem.sellableSizeGroup;
                merged.courseId = merged.courseId ?? menuItem.courseId;
                if (menuItem.productImage && menuItem.productImage.id && !(merged.productImage && merged.productImage.id)) {
                    merged.productImage = menuItem.productImage;
                }
                if (combineSchedules) {
                    merged.mergedSchedules = merged.mergedSchedules.concat(menuItem.mergedSchedules);
                }
            });
            if (sizeGroup != undefined) {
                merged.sellableSizeGroup = sizeGroup;
            }
            menuItems[0].state.mergedItemForSellable = merged;
            return merged;
        };
    });

    public initialiseMenuItem$: Subject<MenuItemData> = new Subject();
    public menuItemInitialised$: Subject<MenuItemData> = new Subject();

    public initialiseItem(menuItem: MenuItemData, initialiseExtras: boolean = true): void {
        this.getMenuItemState(menuItem).initialise(initialiseExtras);
    }

    private isSuppressedItem(menuItem: MenuItemData): boolean {
        return menuItem.hideWhenOrdered && this.orderModel.personallyOrderedSellableIds().has(menuItem.sellable.sellableId);
    }

    private itemHasVisibleExtras(menuItem: MenuItemData): boolean {
        this.initialiseItem(menuItem);
        return menuItem.sellable.extraGroups.length && menuItem.sellable.extraGroups.some((extraGroup: MenuExtraGroupData) => !!this.extraGroupVisibleOptions(extraGroup, menuItem).length);
    }

    private itemHasRequiredExtras(menuItem: MenuItemData): boolean {
        this.initialiseItem(menuItem);
        return menuItem.sellable.extraGroups.length && menuItem.sellable.extraGroups.some((extraGroup: MenuExtraGroupData) => this.extraGroupRequiresChoices(extraGroup, menuItem) || this.extraGroupIsPromoted()(extraGroup));
    }

    public isCompositeItem(menuItem: MenuItemData): boolean {
        return this.isCompositeSellable(menuItem.sellable);
    }

    public isNormalMenuItem(menuItem: MenuItem): boolean {
        return menuItem && menuItem.sellable["name"] != undefined;
    }

    private isCompositeSellable(sellable: MenuItemSellableData): boolean {
        return sellable.composite && !!sellable.composite.sellableId;
    }

    public getCompositeLines(composite: MenuCompositeData): MenuQtyData[] {
        const items: Array<MenuQtyData> = [...composite.prodLineQuantities, ...composite.prodLinePlgQuantities];
        items.sort((a: MenuQtyData, b: MenuQtyData) => a.indexOrder - b.indexOrder);
        return items;
    }

    public getMenuItemDisplayName(menuItem: MenuItem): string {
        return menuItem?.name || menuItem?.sellable?.integratedDisplayName || menuItem?.sellable?.name;
    }

    public getMenuItemDescription(menuItem: MenuItem): string {
        return menuItem?.sellable ? (menuItem?.description ?? this.getSellableExternalDescription(<MenuSellableData>menuItem.sellable)) : undefined;
    }

    public getMenuItemProductImage(menuItem: MenuItem): FedeltaImageData {
        return menuItem?.productImage?.id || menuItem?.productImage?.image ? menuItem.productImage
            : menuItem?.sellable?.integratedProductImage?.id || menuItem?.sellable?.integratedProductImage?.image ? menuItem.sellable.integratedProductImage
                : undefined;
    }

    public getSellableExternalDescription(sellable: MenuSellableData): string {
        return sellable.externalDescription || sellable.integratedExternalDescription;
    }

    private filterTagsMatch(optionTags: string, productTags: string, includeOptionOnUntaggedItems: boolean) {
        const productTagsArr: string[] = productTags ? productTags.split(/\s+/).filter((tag: string) => tag) : [];
        const optionTagsArr: string[] = optionTags ? optionTags.split(/\s+/).filter((tag: string) => tag) : [];

        // If includeOptionOnUntaggedItems is true, both sides must specify filter tags for an option to be filtered out. I.e. no tags specified on either side matches everything.
        // If includeOptionOnUntaggedItems is false, if no tags are specified on the option, this still matches everything, but if no tags are specified on the product, this does not match a tagged option.
        return !optionTagsArr.length ||
            (includeOptionOnUntaggedItems && !productTagsArr.length) ||
            optionTagsArr.some((optionTag: string) => optionTag.split(/\+/).every((andTag: string) => !andTag || productTagsArr.indexOf(andTag) >= 0));
    }

    /* ==================================================================
        MENU ITEM SIZES
       ================================================================== */

    public readonly itemSizes: Signal<(menuItem: MenuItemData) => MenuItemSize[]> = computed<(menuItem: MenuItemData) => MenuItemSize[]>(() => {
        const findSizesForItem: (menuItem: MenuItemData) => MenuItemSize[] = this.findSizesForItem();
        const itemIsAvailableOnSchedules: (menuItem: MenuItemData) => boolean = this.itemIsAvailableOnSchedules();
        return (menuItem: MenuItemData): MenuItemSize[] => {
            const sizes: MenuItemSize[] = findSizesForItem(menuItem).filter((size: MenuItemSize) => itemIsAvailableOnSchedules(size.menuItem));
            return sizes.length ? sizes : undefined;
        };
    });

    public readonly itemSizeQualifier: Signal<(menuItem: MenuItemData) => string> = computed<(menuItem: MenuItemData) => string>(() => {
        const useSizeGroupNames: boolean = this.menuEntityModel.dataSignal()?.useSizeGroupNames;
        const getItemSellableSize: (menuItem: MenuItemData) => SellableSizesData = this.itemSellableSize();
        return (menuItem: MenuItemData): string => {
            if (menuItem.sellableSizeGroup && useSizeGroupNames) {
                const size: SellableSizesData = getItemSellableSize(menuItem);
                if (size && size.externalName) {
                    return size.sizeLabel;
                }
            }
            return undefined;
        };
    });

    public readonly itemSizeSummary: Signal<(menuItem: MenuItemData) => string[]> = computed<(menuItem: MenuItemData) => string[]>(() => {
        const getItemSizes: (menuItem: MenuItemData) => MenuItemSize[] = this.itemSizes();
        const getItemSizeQualifier: (menuItem: MenuItemData) => string = this.itemSizeQualifier();
        return (menuItem: MenuItemData): string[] => {
            const sizes: MenuItemSize[] = getItemSizes(menuItem);
            const qualifier: string = getItemSizeQualifier(menuItem);
            if (sizes && sizes.length >= 2 && qualifier) {
                return sizes.map((size: MenuItemSize) => this.getSizeAbbreviation(size.sellableSize));
            }
            return undefined;
        };
    });

    public readonly itemHasCompositeProductInSizes: Signal<(menuItem: MenuItemData) => boolean> = computed<(menuItem: MenuItemData) => boolean>(() => {
        const findSizesForItem: (menuItem: MenuItemData) => MenuItemSize[] = this.findSizesForItem();
        return (menuItem: MenuItemData): boolean =>
            findSizesForItem(menuItem).some((menuItemSize: MenuItemSize) => this.isCompositeItem(menuItemSize.menuItem));
    });

    public readonly findSellableSizeId: Signal<(sellableSizeGroupId: number, sellableId: number) => number> = computed<(sellableSizeGroupId: number, sellableId: number) => number>(() => {
        const sellableSizes: SellableSizesData[] = this.sellableSizesSearchModel.dataSignal();
        return (sellableSizeGroupId: number, sellableId: number): number => {
            const size: SellableSizesData = sellableSizes
                .filter((size: SellableSizesData) => size.sellableSizeGroupId === sellableSizeGroupId)
                .find((size: SellableSizesData) => size.sellableId === sellableId);
            return size ? size.sellableSizeId : undefined;
        };
    });

    private readonly itemSellableSize: Signal<(menuItem: MenuItemData) => SellableSizesData> = computed<(menuItem: MenuItemData) => SellableSizesData>(() => {
        const sellableSizes: SellableSizesData[] = this.sellableSizesSearchModel.dataSignal();
        return (menuItem: MenuItemData): SellableSizesData => {
            if (!menuItem.sellableSizeGroup) {
                return undefined;
            }
            if (menuItem.sellableSize === undefined) {
                const sellableSize: SellableSizesData = sellableSizes.find((size: SellableSizesData) => size.sellableSizeGroupId === menuItem.sellableSizeGroup && size.sellableId === menuItem.sellable.sellableId);
                menuItem.sellableSize = sellableSize ? sellableSize : null;
            }
            return menuItem.sellableSize;
        };
    });

    private readonly findSizesForItem: Signal<(menuItem: MenuItemData) => MenuItemSize[]> = computed<(menuItem: MenuItemData) => MenuItemSize[]>(() => {
        const sellableSizes: SellableSizesData[] = this.sellableSizesSearchModel.dataSignal();
        const findMergedItem: (params: FindMergedItemForSellableParams) => MenuItemData = this.findMergedItemForSellable();
        const orderType: OrderType = this.orderingContextModel.orderType();
        return (menuItem: MenuItemData): MenuItemSize[] => {
            const menuItemSizes: MenuItemSize[] = [];

            // Find the sellable sizes for a given sellable id within the specified size group.
            // Unfortunately, the current model is wrong in that menu items are associated with a size group rather than a size relation group.
            // This means that in theory, a given product could be in more than one size relation group for the specified size group.
            // We pick the first size relation group we encounter for the sellable in the specified size group, and use
            // sizes only from that size relation group (groupId).
            const sizesForSizeGroup: SellableSizesData[] = sellableSizes.filter((size: SellableSizesData) => size.sellableSizeGroupId === menuItem.sellableSizeGroup);
            const sizeForSellable: SellableSizesData = sizesForSizeGroup.find((size: SellableSizesData) => size.sellableId === menuItem.sellable.sellableId);
            const sizes: SellableSizesData[] = sizeForSellable ? sizesForSizeGroup.filter((size: SellableSizesData) => size.groupId === sizeForSellable.groupId) : [];

            if (sizes) {
                // We want to find the menu items for each of the sellable sizes in the chosen size relation group.
                // Unfortunately, the current model is wrong in that menu items are associated with a size group rather than a size relation group.
                // The best we can do, then, is find the menu items for the sellable in the same size group. Note that this would not be the expected behaviour
                // had someone intentionally placed a product into multiple size relation groups for the same size group.
                // The risk here is that we choose a menu item with schedule restrictions that make it unavailable when checked during order placement,
                // even though other menu items for the same sellable and in the same size group are available.
                // In fact, this could still happen even if the menu items were properly associated with size relation groups.
                // To mitigate this issue, we find all menu items for the sellable and return a merged menu item reflecting the combined details,
                // so long as at least one menu item is in the required size group.
                sizes.forEach((size: SellableSizesData) => {
                    const groupMenuItem: MenuItemData = findMergedItem({
                        sellableId: size.sellableId,
                        orderType,
                        sizeGroup: size.sellableSizeGroupId,
                        allowOutOfStock: true,
                        initialiseExtras: false
                    });
                    if (groupMenuItem) {
                        menuItemSizes.push(new MenuItemSize(size, groupMenuItem));
                    }
                });
            }
            return menuItemSizes;
        };
    });

    private readonly findAnyExtraItemsMenuItemForSellable: Signal<(sellableId: number) => MenuExtraItemsMenuItemData> = computed<(sellableId: number) => MenuExtraItemsMenuItemData>(() => {
        const extraItems: MenuExtraItemsData[] = this.menuEntityModel.dataSignal()?.extraItems;
        return (sellableId: number): MenuExtraItemsMenuItemData => {
            let menuItem: MenuExtraItemsMenuItemData = null;
            extraItems.some((extraItemsGroup: MenuExtraItemsData) => {
                menuItem = extraItemsGroup.menuItems.find((item: MenuExtraItemsMenuItemData) => item.sellable.sellableId === sellableId);
                return menuItem != undefined;
            });
            return menuItem;
        };
    });

    public getSizeName(sizes: SellableSizesData[]): string {
        return sizes[0].externalSizeName || sizes[0].externalName || sizes[0].groupName;
    }

    public getSizeAbbreviation(size: SellableSizesData): string {
        if (size.sizeAbbreviation) {
            return size.sizeAbbreviation;
        }
        switch (size.sizeLabel) {
            case "Schooner":
                return "Sch";
            case "Pint":
                return "Pnt";
            case "Imperial Pint":
                return "Imp";
            case "Glass":
                return "Gls";
            case "Bottle":
                return "Btl";
            case "Small":
                return "S";
            case "Regular":
                return "R";
            case "Medium":
                return "M";
            case "Large":
                return "L";
            case "Short":
                return "S";
            case "Tall":
                return "T";
            case "Grande":
                return "G";
            case "Venti":
                return "V";
            default:
                return size.sizeLabel;
        }
    }

    /* ==================================================================
        MENU ITEM EXTRAS
       ================================================================== */

    public readonly extraSizes: Signal<(sellableId: number, extraGroup: MenuExtraGroupData) => SellableSizesData[]> = computed<(sellableId: number, extraGroup: MenuExtraGroupData) => SellableSizesData[]>(() => {
        const sellableSizes: SellableSizesData[] = this.sellableSizesSearchModel.dataSignal();
        return (sellableId: number, extraGroup: MenuExtraGroupData): SellableSizesData[] => {
            const sizes: SellableSizesData[] = sellableSizes.filter((size) => size.sellableId === sellableId &&
                extraGroup.extraProducts.find((value) => value.productLine.sellableId === sellableId) != undefined);
            return sizes.length ? sellableSizes.filter((size) => size.groupId === sizes[0].groupId &&
                extraGroup.extraProducts.find((value) => value.productLine.sellableId === size.sellableId) != undefined) : undefined;
        };
    });

    public readonly extraLimit: Signal<(extraProduct: MenuExtraProductData) => number> = computed<(extraProduct: MenuExtraProductData) => number>(() => {
        const defaultExtrasLimit: number = this.menuEntityModel.dataSignal()?.defaultExtrasLimit;
        return (extraProduct: MenuExtraProductData): number => {
            // Limit of zero means explicitly unlimited
            if (extraProduct.qtyLimit === 0) {
                return 0;
            }

            let limit: number = extraProduct.qtyLimit || defaultExtrasLimit || 1;
            if (extraProduct.defaultOrderQty) {
                limit = Math.max(limit, extraProduct.defaultOrderQty);
            }
            return limit;
        };
    });

    public readonly extraPrice: Signal<(extraProduct: MenuExtraProductData, extraGroup: MenuExtraGroupData, basePriceForGroup: number) => number> = computed<(extraProduct: MenuExtraProductData, extraGroup: MenuExtraGroupData, basePriceForGroup: number) => number>(() => {
        const showTotalIncludedExtras: boolean = this.configurationModel.app()?.showTotalIncludedExtras;
        return (extraProduct: MenuExtraProductData, extraGroup: MenuExtraGroupData, basePriceForGroup: number): number => {
            if (OrderModel.SHOW_RELATIVE_PRICING_FOR_EXTRAS) {
                const price: number = extraProduct.price - basePriceForGroup;
                return price === 0 ? undefined : price;
            }
            return extraProduct.price <= 0 || showTotalIncludedExtras ? extraProduct.price : Math.max(0, extraProduct.price - (extraGroup.totalIncluded ?? 0));
        };
    });

    public readonly extraGroupIsPromoted: Signal<(extraGroup: MenuExtraGroupData) => boolean> = computed<(extraGroup: MenuExtraGroupData) => boolean>(() => {
        const isConsumerActor: boolean = this.configurationModel.isConsumerActor();
        return (extraGroup: MenuExtraGroupData): boolean =>
            !this.extraGroupIsSingleSelect(extraGroup) && (isConsumerActor ? extraGroup.promoted : extraGroup.promotedForWaiter);
    });

    public readonly extraItemIsAvailableForStore: Signal<(extraItemMenuItem: MenuExtraItemsMenuItemData, store?: StoreData) => boolean> = computed<(extraItemMenuItem: MenuExtraItemsMenuItemData, store?: StoreData) => boolean>(() => {
        const currentStore: StoreData = this.orderingContextModel.currentStore();
        return (extraItemMenuItem: MenuExtraItemsMenuItemData, store: StoreData = currentStore): boolean =>
            !extraItemMenuItem || !store ||
            ((!extraItemMenuItem.stores || !extraItemMenuItem.stores.length || extraItemMenuItem.stores.some((storeId: number) => storeId === store.storeId)) &&
                (!extraItemMenuItem.exclStores || !extraItemMenuItem.exclStores.length || !extraItemMenuItem.exclStores.some((storeId: number) => storeId === store.storeId)));
    });

    public readonly extraItemIsAvailableForMember: Signal<(extraItemMenuItem: MenuExtraItemsMenuItemData, membershipTypeId?: number) => boolean> = computed<(extraItemMenuItem: MenuExtraItemsMenuItemData, membershipTypeId?: number) => boolean>(() => {
        const currentMembershipTypeId: number = this.identityModel.currentMembershipTypeId();
        return (extraItemMenuItem: MenuExtraItemsMenuItemData, membershipTypeId: number = currentMembershipTypeId): boolean =>
            !extraItemMenuItem ||
            ((!extraItemMenuItem.membershipTypeIds || !extraItemMenuItem.membershipTypeIds.length || extraItemMenuItem.membershipTypeIds.indexOf(membershipTypeId || -1) >= 0) &&
                (!extraItemMenuItem.exclMembershipTypeIds || !extraItemMenuItem.exclMembershipTypeIds.length || extraItemMenuItem.exclMembershipTypeIds.indexOf(membershipTypeId || -1) < 0));
    });

    public readonly extraItemIsAvailableForOrderType: Signal<(extraItemMenuItem: MenuExtraItemsMenuItemData, orderType?: OrderType) => boolean> = computed<(extraItemMenuItem: MenuExtraItemsMenuItemData, orderType?: OrderType) => boolean>(() => {
        const currentOrderType: OrderType = this.orderingContextModel.orderType();
        return (extraItemMenuItem: MenuExtraItemsMenuItemData, orderType: OrderType = currentOrderType): boolean =>
            !extraItemMenuItem || !orderType ||
            ((!extraItemMenuItem.orderTypes || !extraItemMenuItem.orderTypes.length || extraItemMenuItem.orderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))) &&
                (!extraItemMenuItem.exclOrderTypes || !extraItemMenuItem.exclOrderTypes.length || !extraItemMenuItem.exclOrderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))));
    });

    public readonly getSingleOptionExtraLimit: Signal<(extraGroup: MenuExtraGroupData) => number> = computed<(extraGroup: MenuExtraGroupData) => number>(() => {
        const getExtraLimitFn: (extraProduct: MenuExtraProductData) => number = this.extraLimit();
        return (extraGroup: MenuExtraGroupData): number => {
            if (!this.extraGroupHasSingleOption(extraGroup)) {
                return undefined;
            }

            // If there is only one option, the group qty limit also applies to that option.
            const extraLimit: number = getExtraLimitFn(extraGroup.extraProducts[0]);
            const groupQtyLimit: number = this.getExtraGroupMaxQty(extraGroup);
            return extraLimit && groupQtyLimit ? Math.min(extraLimit, groupQtyLimit) : (extraLimit ?? groupQtyLimit);
        };
    });

    public readonly extraItemIsAvailable: Signal<(extraItemMenuItem: MenuExtraItemsMenuItemData, productMenuItem: MenuItemData, selectedFilterTags?: string) => boolean> = computed<(extraItemMenuItem: MenuExtraItemsMenuItemData, productMenuItem: MenuItemData, selectedFilterTags?: string) => boolean>(() => {
        const extraItemIsAvailableForStore: (extraItemMenuItem: MenuExtraItemsMenuItemData, store?: StoreData) => boolean = this.extraItemIsAvailableForStore();
        const extraItemIsAvailableForMember: (extraItemMenuItem: MenuExtraItemsMenuItemData, membershipTypeId?: number) => boolean = this.extraItemIsAvailableForMember();
        const extraItemIsAvailableForOrderType: (extraItemMenuItem: MenuExtraItemsMenuItemData, orderType?: OrderType) => boolean = this.extraItemIsAvailableForOrderType();
        const extraItemIsAvailableOnSchedules: (extraItemMenuItem: MenuExtraItemsMenuItemData) => boolean = this.extraItemIsAvailableOnSchedules();
        return (extraItemMenuItem: MenuExtraItemsMenuItemData, productMenuItem: MenuItemData, selectedFilterTags?: string): boolean =>
            !(extraItemMenuItem && extraItemMenuItem.hidden) &&
            extraItemIsAvailableForStore(extraItemMenuItem) &&
            extraItemIsAvailableForMember(extraItemMenuItem) &&
            extraItemIsAvailableForOrderType(extraItemMenuItem) &&
            extraItemIsAvailableOnSchedules(extraItemMenuItem) &&
            this.extraItemIsAvailableForFilters(extraItemMenuItem, productMenuItem, selectedFilterTags);
    });

    public readonly extraGroupMaxQty: Signal<(extraGroup: MenuExtraGroupData) => number> = computed<(extraGroup: MenuExtraGroupData) => number>(() => {
        const getSingleOptionExtraLimit: (extraGroup: MenuExtraGroupData) => number = this.getSingleOptionExtraLimit();
        return (extraGroup: MenuExtraGroupData): number =>
            this.extraGroupHasSingleOption(extraGroup) ? getSingleOptionExtraLimit(extraGroup) : this.getExtraGroupMaxQty(extraGroup);
    });

    public readonly extraGroupHasNoChoice: Signal<(extraGroup: MenuExtraGroupData) => boolean> = computed<(extraGroup: MenuExtraGroupData) => boolean>(() => {
        const getSingleOptionExtraLimit: (extraGroup: MenuExtraGroupData) => number = this.getSingleOptionExtraLimit();
        return (extraGroup: MenuExtraGroupData): boolean =>
            this.extraGroupHasSingleOption(extraGroup) && (this.getExtraGroupMinQty(extraGroup) === getSingleOptionExtraLimit(extraGroup));
    });

    public readonly extraGroupHasNoChoiceWithDefaultAtLimit: Signal<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => boolean> = computed<(extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData) => boolean>(() => {
        const extraGroupHasNoChoice: (extraGroup: MenuExtraGroupData) => boolean = this.extraGroupHasNoChoice();
        return (extraGroup: MenuExtraGroupData, extraProduct: MenuExtraProductData): boolean =>
            extraGroupHasNoChoice(extraGroup) && extraProduct.defaultOrderQty === this.getExtraGroupMinQty(extraGroup);
    });

    public readonly extraItemIsAvailableOnSchedules: Signal<(extraItemMenuItem: MenuExtraItemsMenuItemData) => boolean> = computed<(extraItemMenuItem: MenuExtraItemsMenuItemData) => boolean>(() => {
        const anyMergedScheduleIsActive: (mergedSchedules: MergedScheduleData[]) => boolean = this.anyMergedScheduleIsActive();
        return (extraItemMenuItem: MenuExtraItemsMenuItemData): boolean =>
            !extraItemMenuItem || anyMergedScheduleIsActive(extraItemMenuItem.mergedSchedules);
    });

    public showExtrasSelected(extraGroup: MenuExtraGroupData): boolean {
        const groupQtyLimit: number = this.getExtraGroupMaxQty(extraGroup);
        return !this.extraGroupIsSingleSelect(extraGroup) && (this.getExtraGroupMinQty(extraGroup) || groupQtyLimit) && !(this.extraGroupHasSingleOption(extraGroup) && groupQtyLimit === 1);
    }

    public getExtraGroupDisplayName(extraGroup: MenuExtraGroupData): string {
        return extraGroup?.externalName || extraGroup?.integratedDisplayName || extraGroup?.name;
    }

    public getExtraGroupMinQty(extraGroup: MenuExtraGroupData): number {
        return extraGroup?.groupQtyMin ?? extraGroup?.integratedMinQty;
    }

    public getExtraGroupMaxQty(extraGroup: MenuExtraGroupData): number {
        const effectiveLimit: number = extraGroup.groupQtyLimit ?? extraGroup.integratedMaxQty;
        return effectiveLimit == undefined ? undefined : Math.max(effectiveLimit, this.getTotalDefaultOrderQty(extraGroup));
    }

    private getTotalDefaultOrderQty(extraGroup: MenuExtraGroupData): number {
        return extraGroup.extraProducts.reduce((total: number, extraProduct: MenuExtraProductData) => total + (extraProduct.defaultOrderQty ?? 0), 0);
    }

    public extraGroupIsOptional(extraGroup: MenuExtraGroupData): boolean {
        return !this.getExtraGroupMinQty(extraGroup);
    }

    public extraGroupIsSingleSelect(extraGroup: MenuExtraGroupData): boolean {
        return this.getExtraGroupMinQty(extraGroup) === 1 && this.getExtraGroupMaxQty(extraGroup) === 1;
    }

    public extraGroupIsVisible(extraGroup: MenuExtraGroupData): boolean {
        return this.getExtraGroupMaxQty(extraGroup) !== 0 &&
            !(this.extraGroupIsSingleSelect(extraGroup) && extraGroup.extraProducts.length == 1 && extraGroup.extraProducts[0].defaultOrderQty >= 1);
    }

    public getParentExtraGroupId(extraGroup: MenuExtraGroupData, sellable: MenuSellableData): number {
        return extraGroup.usages?.find((usage: MenuProductExtraUsage) => usage.productLineId === sellable.sellableId)?.parentExtraId ?? undefined;
    }

    public extraGroupHasSingleOption(extraGroup: MenuExtraGroupData): boolean {
        return extraGroup.extraProducts.length === 1;
    }

    public extraGroupHasDefaultOptions(extraGroup: MenuExtraGroupData): boolean {
        return !!extraGroup.extraProducts.find((extraProduct: MenuExtraProductData) => {
            if (extraProduct.defaultOrderQty > 0) {
                return true;
            }
            const name: string = (this.getMenuItemDisplayName(extraProduct.menuItem) || extraProduct.productLine.name).toLowerCase();
            return name.startsWith("no ") || name.startsWith("remove ") || name.startsWith("without ") || name.startsWith("w/out ");
        });
    }

    private extraGroupVisibleOptions(extraGroup: MenuExtraGroupData, menuItem: MenuItemData): MenuExtraProductData[] {
        return extraGroup.extraProducts.filter((extraProduct: MenuExtraProductData) =>
            (!extraProduct.menuItem && !menuItem.filterExtras) ||
            (extraProduct.menuItem && !extraProduct.menuItem.hidden && this.extraItemIsAvailableForFilters(extraProduct.menuItem, menuItem)));
    }

    private extraGroupRequiresChoices(extraGroup: MenuExtraGroupData, menuItem: MenuItemData): boolean {
        return !this.extraGroupIsOptional(extraGroup) &&
            this.getExtraGroupMinQty(extraGroup) > this.extraGroupVisibleOptions(extraGroup, menuItem).reduce((total: number, extraProduct: MenuExtraProductData) => total + (extraProduct.defaultOrderQty ?? 0), 0);
    }

    public getExtraProductDisplayName(extraProduct: MenuExtraProductData): string {
        return this.getMenuItemDisplayName(extraProduct?.menuItem) || extraProduct?.productLine?.integratedDisplayName || extraProduct?.productLine.name;
    }

    public getExtraProductImage(extraProduct: MenuExtraProductData): FedeltaImageData {
        return this.getMenuItemProductImage(extraProduct.menuItem) || extraProduct?.productLine?.integratedProductImage;
    }

    public extraItemIsAvailableForFilters(extraItemMenuItem: MenuExtraItemsMenuItemData, productMenuItem: MenuItemData, selectedFilterTags?: string): boolean {
        return !productMenuItem.filterExtras || (extraItemMenuItem && this.filterTagsMatch(extraItemMenuItem.filterTags, (productMenuItem.filterTags || "") + (selectedFilterTags || ""), extraItemMenuItem.includeOptionOnUntaggedItems));
    }

    public getDefaultExtrasPriceForSellable(sellable: MenuItemSellableData): number {
        return this.isCompositeSellable(sellable) ? this.getDefaultExtrasPriceForComposite(sellable.composite) : this.getDefaultExtrasPriceForProductLine(sellable);
    }

    public getDefaultExtrasPriceForProductLine(sellable: MenuSellableData): number {
        return sellable.extraGroups.reduce((total: number, extraGroup: MenuExtraGroupData) => total + this.getDefaultPriceForExtraGroup(extraGroup), 0);
    }

    public getDefaultExtrasPriceForComposite(composite: MenuCompositeData): number {
        return composite.prodLineQuantities.reduce((total: number, prodLineQty: MenuProductLineQtyData) => total + this.getDefaultExtrasPriceForSellable(<MenuItemSellableData>prodLineQty.productLine), 0) +
            composite.prodLinePlgQuantities.reduce((total: number, prodLinePlgQty: MenuProductLinePlgQtyData) => total + this.getDefaultExtrasPriceForPlg(prodLinePlgQty.plg), 0);
    }

    public getDefaultExtrasPriceForPlg(plg: MenuPlgData): number {
        return plg.sellableLines
            .filter((plgSellable: MenuPlgSellableData) => plgSellable.defaultSellable)
            .reduce((total: number, plgSellable: MenuPlgSellableData) => total + (plgSellable.additionalPrice ?? 0) + this.getDefaultExtrasPriceForProductLine(plgSellable.sellable), 0);
    }

    public getDefaultPriceForExtraGroup(extraGroup: MenuExtraGroupData): number {
        return extraGroup.extraProducts?.length ? Math.max(0, extraGroup.extraProducts
            .filter((extraProduct: MenuExtraProductData) => extraProduct.defaultOrderQty)
            .reduce((total: number, extraProduct: MenuExtraProductData) => total + (extraProduct.price * extraProduct.defaultOrderQty), 0) - (extraGroup.totalIncluded ?? 0)) : 0;
    }

    /* ==================================================================
        COMPOSITE OPTION GROUPS (PLGs)
       ================================================================== */

    public readonly findAnyPlgItemsMenuItemForSellable: Signal<(sellableId: number) => MenuPlgItemsMenuItemData> = computed<(sellableId: number) => MenuPlgItemsMenuItemData>(() => {
        const plgItems: MenuPlgItemsData[] = this.menuEntityModel.dataSignal()?.plgItems;
        return (sellableId: number): MenuPlgItemsMenuItemData => {
            let menuItem: MenuPlgItemsMenuItemData = null;
            plgItems.some((plgItemsGroup: MenuPlgItemsData) => {
                menuItem = plgItemsGroup.menuItems.find((item: MenuPlgItemsMenuItemData) => item.sellable.sellableId === sellableId);
                return menuItem != undefined;
            });
            return menuItem;
        };
    });

    public readonly plgItemIsAvailableForStore: Signal<(plgItemMenuItem: MenuPlgItemsMenuItemData, store?: StoreData) => boolean> = computed<(plgItemMenuItem: MenuPlgItemsMenuItemData, store?: StoreData) => boolean>(() => {
        const currentStore: StoreData = this.orderingContextModel.currentStore();
        return (plgItemMenuItem: MenuPlgItemsMenuItemData, store: StoreData = currentStore): boolean =>
            !plgItemMenuItem || !store ||
            ((!plgItemMenuItem.stores || !plgItemMenuItem.stores.length || plgItemMenuItem.stores.some((storeId: number) => storeId === store.storeId)) &&
                (!plgItemMenuItem.exclStores || !plgItemMenuItem.exclStores.length || !plgItemMenuItem.exclStores.some((storeId: number) => storeId === store.storeId)));
    });

    public readonly plgItemIsAvailableForMember: Signal<(plgItemMenuItem: MenuPlgItemsMenuItemData, membershipTypeId?: number) => boolean> = computed<(plgItemMenuItem: MenuPlgItemsMenuItemData, membershipTypeId?: number) => boolean>(() => {
        const currentMembershipTypeId: number = this.identityModel.currentMembershipTypeId();
        return (plgItemMenuItem: MenuPlgItemsMenuItemData, membershipTypeId: number = currentMembershipTypeId): boolean =>
            !plgItemMenuItem ||
            ((!plgItemMenuItem.membershipTypeIds || !plgItemMenuItem.membershipTypeIds.length || plgItemMenuItem.membershipTypeIds.indexOf(membershipTypeId || -1) >= 0) &&
                (!plgItemMenuItem.exclMembershipTypeIds || !plgItemMenuItem.exclMembershipTypeIds.length || plgItemMenuItem.exclMembershipTypeIds.indexOf(membershipTypeId || -1) < 0));
    });

    public readonly plgItemIsAvailableForOrderType: Signal<(plgItemMenuItem: MenuPlgItemsMenuItemData, orderType?: OrderType) => boolean> = computed<(plgItemMenuItem: MenuPlgItemsMenuItemData, orderType?: OrderType) => boolean>(() => {
        const currentOrderType: OrderType = this.orderingContextModel.orderType();
        return (plgItemMenuItem: MenuPlgItemsMenuItemData, orderType: OrderType = currentOrderType): boolean =>
            !plgItemMenuItem || !orderType ||
            ((!plgItemMenuItem.orderTypes || !plgItemMenuItem.orderTypes.length || plgItemMenuItem.orderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))) &&
                (!plgItemMenuItem.exclOrderTypes || !plgItemMenuItem.exclOrderTypes.length || !plgItemMenuItem.exclOrderTypes.some((orderTypeInfo: OrderTypeInfoData) => orderTypeInfo.orderType === OrderType.toString(orderType))));
    });

    public readonly plgItemIsAvailable: Signal<(plgItemMenuItem: MenuPlgItemsMenuItemData, compositeMenuItem: MenuItemData, selectedFilterTags?: string) => boolean> = computed<(plgItemMenuItem: MenuPlgItemsMenuItemData, compositeMenuItem: MenuItemData, selectedFilterTags?: string) => boolean>(() => {
        const plgItemIsAvailableForStoreFn: (plgItemMenuItem: MenuPlgItemsMenuItemData, store?: StoreData) => boolean = this.plgItemIsAvailableForStore();
        const plgItemIsAvailableForMemberFn: (plgItemMenuItem: MenuPlgItemsMenuItemData, membershipTypeId?: number) => boolean = this.plgItemIsAvailableForMember();
        const plgItemIsAvailableForOrderTypeFn: (plgItemMenuItem: MenuPlgItemsMenuItemData, orderType?: OrderType) => boolean = this.plgItemIsAvailableForOrderType();
        return (plgItemMenuItem: MenuPlgItemsMenuItemData, compositeMenuItem: MenuItemData, selectedFilterTags?: string): boolean =>
            !(plgItemMenuItem && plgItemMenuItem.hidden) &&
            plgItemIsAvailableForStoreFn(plgItemMenuItem) &&
            plgItemIsAvailableForMemberFn(plgItemMenuItem) &&
            plgItemIsAvailableForOrderTypeFn(plgItemMenuItem) &&
            this.plgItemIsAvailableOnSchedules()(plgItemMenuItem) &&
            this.plgItemIsAvailableForFilters(plgItemMenuItem, compositeMenuItem, selectedFilterTags);
    });

    public readonly plgItemIsAvailableOnSchedules: Signal<(plgItemMenuItem: MenuPlgItemsMenuItemData) => boolean> = computed<(plgItemMenuItem: MenuPlgItemsMenuItemData) => boolean>(() => {
        const anyMergedScheduleIsActiveFn: (mergedSchedules: MergedScheduleData[]) => boolean = this.anyMergedScheduleIsActive();
        return (plgItemMenuItem: MenuPlgItemsMenuItemData): boolean =>
            !plgItemMenuItem || anyMergedScheduleIsActiveFn(plgItemMenuItem.mergedSchedules);
    });

    public plgItemIsAvailableForFilters(plgItemMenuItem: MenuPlgItemsMenuItemData, compositeMenuItem: MenuItemData, selectedFilterTags?: string): boolean {
        return !compositeMenuItem.filterPlgItems || (plgItemMenuItem && this.filterTagsMatch(plgItemMenuItem.filterTags, (compositeMenuItem.filterTags || "") + (selectedFilterTags || ""), plgItemMenuItem.includeOptionOnUntaggedItems));
    }

    public isProductLineQty(compositeItem: MenuQtyData): boolean {
        return compositeItem && compositeItem["productLine"] != undefined;
    }

    public isProductLinePlgQty(compositeItem: MenuQtyData): boolean {
        return compositeItem && compositeItem["plg"] != undefined;
    }

    /* ==================================================================
        MENU ITEM SCHEDULES
       ================================================================== */

    private readonly anyScheduleIsActive: Signal<(schedules: ScheduleData[]) => boolean> = computed<(schedules: ScheduleData[]) => boolean>(() => {
        const activeSchedules: ScheduleData[] = this.activeSchedulesSearchModel.dataSignal();
        return (schedules: ScheduleData[]): boolean =>
            !schedules || !schedules.length ||
            schedules.some((schedule: ScheduleData) => activeSchedules.some((activeSchedule: ScheduleData) => schedule.id == activeSchedule.id));
    });

    private readonly noScheduleIsActive: Signal<(schedules: ScheduleData[]) => boolean> = computed<(schedules: ScheduleData[]) => boolean>(() => {
        const activeSchedules: ScheduleData[] = this.activeSchedulesSearchModel.dataSignal();
        return (schedules: ScheduleData[]): boolean =>
            !schedules || !schedules.length ||
            !schedules.some((schedule: ScheduleData) => activeSchedules.some((activeSchedule: ScheduleData) => schedule.id == activeSchedule.id));
    });

    private readonly anyMergedScheduleIsActive: Signal<(mergedSchedules: MergedScheduleData[]) => boolean> = computed<(mergedSchedules: MergedScheduleData[]) => boolean>(() => {
        const anyScheduleIsActiveFn: (schedules: ScheduleData[]) => boolean = this.anyScheduleIsActive();
        const noScheduleIsActiveFn: (schedules: ScheduleData[]) => boolean = this.noScheduleIsActive();
        return (mergedSchedules: MergedScheduleData[]): boolean =>
            !mergedSchedules || !mergedSchedules.length ||
            mergedSchedules.some((mergedSchedule: MergedScheduleData) =>
                anyScheduleIsActiveFn(mergedSchedule.itemSchedules) &&
                noScheduleIsActiveFn(mergedSchedule.exclItemSchedules) &&
                anyScheduleIsActiveFn(mergedSchedule.categorySchedules) &&
                noScheduleIsActiveFn(mergedSchedule.exclCategorySchedules) &&
                anyScheduleIsActiveFn(mergedSchedule.primaryCategorySchedules) &&
                noScheduleIsActiveFn(mergedSchedule.exclPrimaryCategorySchedules) &&
                anyScheduleIsActiveFn(mergedSchedule.submenuSchedules) &&
                noScheduleIsActiveFn(mergedSchedule.exclSubmenuSchedules));
    });

    private hasSchedule(schedules: ScheduleData[]): boolean {
        return schedules && !!schedules.length;
    }

    private itemIsScheduled(menuItem: MenuItemData): boolean {
        return menuItem.mergedSchedules && menuItem.mergedSchedules.length &&
            menuItem.mergedSchedules.every((mergedSchedule: MergedScheduleData) =>
                this.hasSchedule(mergedSchedule.itemSchedules) ||
                this.hasSchedule(mergedSchedule.exclItemSchedules) ||
                this.hasSchedule(mergedSchedule.categorySchedules) ||
                this.hasSchedule(mergedSchedule.exclCategorySchedules) ||
                this.hasSchedule(mergedSchedule.primaryCategorySchedules) ||
                this.hasSchedule(mergedSchedule.exclPrimaryCategorySchedules) ||
                this.hasSchedule(mergedSchedule.submenuSchedules) ||
                this.hasSchedule(mergedSchedule.exclSubmenuSchedules));
    }

    /* ==================================================================
        MENU ITEM SEARCH
       ================================================================== */

    private static stopWords: string[] = [
        "the", "a", "an", "and", "with", "on", "in", "of", "at", "for",
        "or", "to", "from", "by", "is", "are", "it", "that", "this", "which"
    ];

    private static synonyms: { [key: string]: string[] } = {
        "chips": ["fries", "wedges"],
        "fries": ["chips", "wedges"],
        "wedges": ["fries", "chips"],
        "capsicum": ["pepper"],
        "pepper": ["capsicum"]
    };

    private menuItemKeywordIndex: Map<string, IndexedMenuItem[]> = new Map();

    private buildKeywordIndex(): void {
        this.categoryItems().forEach((menuItems: MenuItemData[], category: MenuCategoryData) => {
            if (category.pairingsCategory) {
                return;
            }
            menuItems.forEach((menuItem: MenuItemData) => {
                const itemInCategory: MenuItemInCategory = new MenuItemInCategory(menuItem, category);
                this.getItemKeywords(menuItem, category).forEach((weighting: number, keyWord: string) => {
                    let indexedMenuItems: IndexedMenuItem[] = this.menuItemKeywordIndex.get(keyWord);
                    if (!indexedMenuItems) {
                        indexedMenuItems = [];
                        this.menuItemKeywordIndex.set(keyWord, indexedMenuItems);
                    }
                    indexedMenuItems.push(new IndexedMenuItem(itemInCategory, weighting));
                });
            });
        });
    }

    private getItemKeywords(menuItem: MenuItemData, category: MenuCategoryData): Map<string, number> {
        const keyWords: Map<string, number> = new Map();

        menuItem.sellable.tags.map((tag: MenuSellableMetaTagDefinitionData) => tag.name)
            .forEach((tagName: string) => this.getKeywordsFromText(tagName).forEach((keyWord: string) => keyWords.set(keyWord, 1)));

        this.getKeywordsFromText(menuItem.sellable.primaryCategory.name).forEach((keyWord: string) => keyWords.set(keyWord, 2));
        menuItem.sellable.primaryCategory.ancestors.map((ancestor: MenuSellableCategoryLookupData) => ancestor.category.name)
            .forEach((categoryName: string) => this.getKeywordsFromText(categoryName).forEach((keyWord: string) => keyWords.set(keyWord, 2)));
        menuItem.sellable.customObjects.map((customObject: MenuSellableCustomObjectData) => customObject.name)
            .forEach((customObjectName: string) => this.getKeywordsFromText(customObjectName).forEach((keyWord: string) => keyWords.set(keyWord, 2)));

        menuItem.sellable.dietaryIndicators.map((indicator: MenuDietaryIndicatorsData) => indicator.name)
            .forEach((indicatorName: string) => this.getKeywordsFromText(indicatorName).forEach((keyWord: string) => keyWords.set(keyWord, 3)));
        this.getKeywordsFromText(category.name).forEach((keyWord: string) => keyWords.set(keyWord, 3));

        this.getKeywordsFromText(this.getMenuItemDisplayName(menuItem)).forEach((keyWord: string) => keyWords.set(keyWord, 4));

        return keyWords;
    }

    private getKeywordsFromText(text: string): string[] {
        const intl: any = Intl; // old definition
        const segments = new intl.Segmenter([], {granularity: "word"}).segment(text.toLowerCase());
        return Array.from(segments)
            .filter((segment: any) => segment.isWordLike)
            .map((segment: any) => segment.segment)
            .filter((word: string) => word.length > 2 && MenuModel.stopWords.indexOf(word) < 0);
    }

    public searchItems(searchText: string): MenuItemInCategory[] {

        // Ignore any Levenshtein distance greater than this:
        const MAX_LEVENSHTEIN_DISTANCE: number = 2;

        const itemScores: Map<MenuItemInCategory, number> = new Map();

        // Extract the matchable words from the search text entered by the user.
        searchText = searchText.trim();
        if (!searchText) {
            return [];
        }
        const searchWords: string[] = this.getKeywordsFromText(searchText);

        // Build the keyword map on demand to minimise overhead when building the menu.
        if (this.menuItemKeywordIndex.size == 0) {
            this.buildKeywordIndex();
        }

        // Iterate over every keyword (and its synonyms) looking for fuzzy matches.
        // A given keyword/synonym can match against a single search word. Find the best match.
        // Higher score is better:
        //   - Exact match = 5
        //   - Starts-with match = 3
        //   - Levenshtein distance 1 = 2
        //   - Levenshtein distance 2 = 1
        // Nothing else counts as a match.
        // The same item can accumulate scores by matching against multiple keywords associated with that item.
        // We sum the scores multiplied by the weighting (the weighting adjusts the score according to which properties of an item the keyword is derived from).
        this.menuItemKeywordIndex.forEach((indexedMenuItems: IndexedMenuItem[], keyWord: string) => {
            let bestScore: number = 0;
            [keyWord, ...MenuModel.synonyms[keyWord] || []].some((synonym: string) => searchWords.some((searchWord: string) => {
                const exactMatch: boolean = searchWord === synonym;
                bestScore = exactMatch ? MAX_LEVENSHTEIN_DISTANCE + 3 : Math.max(bestScore, synonym.startsWith(searchWord) ? (MAX_LEVENSHTEIN_DISTANCE + 1) : (MAX_LEVENSHTEIN_DISTANCE + 1 - Levenshtein.distance(searchWord, synonym)));
                return exactMatch;
            }));
            if (bestScore > 0) {
                indexedMenuItems.forEach((indexedMenuItem: IndexedMenuItem) => itemScores.set(indexedMenuItem.itemInCategory, (itemScores.get(indexedMenuItem.itemInCategory) ?? 0) + (bestScore * indexedMenuItem.weighting)));
            }
        });

        // Return a list of the matched items sorted by total score.
        return Array.from(itemScores, ([itemInCategory, score]: [MenuItemInCategory, number]) => ({
            itemInCategory,
            score
        }))
            .sort(({score: a}, {score: b}) => b - a)
            .map(({itemInCategory: itemInCategory}) => itemInCategory);
    }

    /* ==================================================================
        MENU NAVIGATION
       ================================================================== */

    private static readonly ANCHOR_BAR_POST_SELECT_AUTOSCROLL_SUPPRESSION_TIME_MS: number = 1000;

    public readonly selectedCategorySubcategories: WritableSignal<MenuCategoryData[]> = signal<MenuCategoryData[]>([]);
    public readonly selectedCategoryAnchorNames: WritableSignal<string[]> = signal<string[]>([]);
    public readonly selectedCategoryHasMainCategoryItems: WritableSignal<boolean> = signal<boolean>(false);

    private readonly _selectedCategory: WritableSignal<MenuCategoryData> = signal<MenuCategoryData>(undefined);
    public readonly selectedCategory: Signal<MenuCategoryData> = this._selectedCategory.asReadonly();
    public readonly selectedCategoryName: Signal<string> = computed<string>(() => this._selectedCategory()?.name);
    private readonly _selectedAnchorIndex: WritableSignal<number> = signal<number>(0);
    public readonly selectedAnchorIndex: Signal<number> = this._selectedAnchorIndex.asReadonly();
    private readonly anchorSelectedTime: WritableSignal<number> = signal<number>(0);

    /**
     * Notifies that a category has been selected (or reselected after a reload).
     */
    public readonly categorySelected$: BehaviorSubject<MenuCategorySelection> = new BehaviorSubject(new MenuCategorySelection(undefined, false));

    /**
     * Notifies that a subcategory anchor has been selected.
     * The value indicates whether the selection was directly made by a user (true) or due to an anchor target scrolling into view (false).
     */
    public readonly anchorSelected$: Subject<boolean> = new Subject();

    /**
     * Notifies that the menu items panel is scrolling
     */
    public readonly itemsScrolling$: Subject<void> = new Subject();

    /**
     * Notifies that the subcategory anchor bar should be scrolled to the selected anchor.
     * Note that this event is separate to the selection of the anchor bar, since scrolling of the anchor bar doesn't occur
     * until the target items have finished scrolling.
     */
    public readonly scrollAnchorBar$: Subject<number> = new Subject();

    public selectCategory(selectedCategory: MenuCategoryData, reselecting: boolean = false, event?: MouseEvent): void {
        const selectingSubCategory = this.isSubcategory()(selectedCategory);
        this._selectedCategory.set(selectingSubCategory ? this.subToSuperCategories().get(selectedCategory) : selectedCategory);
        this._selectedAnchorIndex.set(undefined);
        if (!reselecting) {
            this.buildSelectedCategory();
        }
        this.categorySelected$.next(new MenuCategorySelection(this._selectedCategory(), reselecting));

        if (selectingSubCategory) {
            setTimeout(() => this.selectAnchor(this.selectedCategorySubcategories().indexOf(selectedCategory) + (this.selectedCategoryHasMainCategoryItems() ? 1 : 0), false, event), 500);
        }
    }

    private selectDefaultCategory(): void {
        if (!this._selectedCategory() && this._selectableCategories()) {
            this.selectCategory(this._selectableCategories()[0]);
        }
    }

    private buildSelectedCategory(): void {
        this.selectedCategorySubcategories.set([]);
        this.selectedCategoryAnchorNames.set([]);
        this.selectedCategoryHasMainCategoryItems.set(false);

        const category: MenuCategoryData = this._selectedCategory();
        if (category) {
            const subcategories: MenuCategoryData[] = this.isSuperCategory()(category) ? this._categories().filter((subcategory: MenuCategoryData) => this.isSubcategory()(subcategory, category) && this.categoryHasItems()(subcategory)) : [];
            this.selectedCategorySubcategories.set(subcategories);
            this.selectedCategoryAnchorNames.set(subcategories.map((subcategory: MenuCategoryData) => this.getCategoryDisplayName()(subcategory)));

            // Add an extra anchor for the main category if it contains its own items
            const hasMainCategoryItems: boolean = this.categoryItems().has(category) && !!this.categoryItems().get(category).length;
            this.selectedCategoryHasMainCategoryItems.set(hasMainCategoryItems);
            if (hasMainCategoryItems && subcategories.length) {
                this.selectedCategoryAnchorNames.update((names: string[]) => [this.getCategoryDisplayName()(category), ...names]);
            }

            // Super-categories reflect the minimum wait time of their subcategories
            if (subcategories.length) {
                const subCategoriesMinWaitTime: number = Math.min(...(subcategories.map((subcategory: MenuCategoryData) => subcategory.currentWaitTimeMinutes ?? 0)));
                category.currentWaitTimeMinutes = hasMainCategoryItems ? Math.min((category.currentWaitTimeMinutes ?? 0), subCategoriesMinWaitTime) : subCategoriesMinWaitTime;
            }
        }
    }

    public selectAnchor(selectedAnchorIndex: number, scrollAnchorBarImmediately: boolean, event?: MouseEvent): void {
        if (selectedAnchorIndex === this._selectedAnchorIndex() && !event) {
            return;
        }

        // If selecting due to a mouse event, record the time to prevent selection of intermediate anchors during item scrolling.
        if (event) {
            this.anchorSelectedTime.set(Date.now());
        }

        // Even if no items intersections change, we need a final event to indicate that item scrolling has completed.
        if (!scrollAnchorBarImmediately) {
            this.itemsScrolling$.next();
        }

        this._selectedAnchorIndex.set(selectedAnchorIndex);
        this.anchorSelected$.next(!!event);
        if (scrollAnchorBarImmediately) {
            this.scrollAnchorBar$.next(this._selectedAnchorIndex());
        }

        const category: MenuCategoryData = this.selectedCategorySubcategories()[selectedAnchorIndex - (this.selectedCategoryHasMainCategoryItems() ? 1 : 0)];
        if (category && category.pairingsCategory) {
            void this.analyticsService.trackPairingsShown();
        }
    }

    public get canAutoScrollAnchorBar() {
        return Date.now() - this.anchorSelectedTime() >= MenuModel.ANCHOR_BAR_POST_SELECT_AUTOSCROLL_SUPPRESSION_TIME_MS;
    }
}
